import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import supabase from '../utils/supabaseUtils';
import { logUserUpload, updateUserProfile, changeUserPassword, getUserPreferences } from '../utils/supabaseUtils';
import { addWatermark, addLogoWatermark } from '../utils/imageUtils';
import DashboardEditor from './DashboardEditor';
import AdminDashboardView from './AdminDashboardView';
import UserOrdersHistory from './UserOrdersHistory';
import TutorialModal from './TutorialModal';
import './Dashboard.css';
import './MediaDayPics.css'; // Import the new styles for Media Day Pics
import { useToast } from '@chakra-ui/react';
import stackedLogo from '../assets/playmaker-logo-stacked-w-tag.png';
import ErrorBoundary from './ErrorBoundary';
import { FaShoppingCart, FaTrash, FaQuestionCircle, FaInstagram, FaFacebook, FaLinkedin, FaPinterest, FaYoutube, FaDownload as FaDownloadIcon } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { SimpleGrid, IconButton, Popover, PopoverTrigger, PopoverContent, PopoverArrow, PopoverCloseButton } from '@chakra-ui/react';
import { useCart } from '../context/CartContext';
import { Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton, ModalFooter, VStack, Image, Text, Button } from '@chakra-ui/react';
import { SiTiktok, SiSnapchat } from 'react-icons/si';
//import DebugJWT from './DebugJWT';
import { trackEvent } from '../utils/analyticsUtils';
import EventSwitcher from './EventSwitcher';
import { useCurrentEvent } from '../context/CurrentEventContext';
import { Box, Badge, HStack, Heading } from '@chakra-ui/react';

const removeBgApiKey = import.meta.env.VITE_REMOVE_BG_API_KEY;

const Dashboard = ({ activeSection: propActiveSection, setActiveSection: propSetActiveSection }) => {
  // Add check if we're on the editor page
  const isEditorPage = window.location.pathname.includes('/editor');
  // Add check if we're on checkout pages
  const isCheckoutPage = window.location.pathname.includes('/checkout') || 
                         window.location.pathname.includes('/stripe-checkout');
  
  // Get current location for tutorial checks
  const location = useLocation();
  
  // Impersonation state
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [originalAdminId, setOriginalAdminId] = useState(null);
  
  // Tutorial state
  const [showTutorial, setShowTutorial] = useState(false);
  
  // Editor state
  const [cesdk, setCesdk] = useState(null);
  
  // User and admin state
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // State variables
  const [sourceImages, setSourceImages] = useState([]);
  const [primaryImage, setPrimaryImage] = useState(null);
  const [uploadedImageURL, setUploadedImageURL] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formData, setFormData] = useState({});
  const [exportedImage, setExportedImage] = useState(null);
  const [placeholderFields, setPlaceholderFields] = useState([]);
  // State for CESDK placeholders
  const [placeholderData, setPlaceholderData] = useState([]);
  const [showTemplateBrowser, setShowTemplateBrowser] = useState(false);
  // State for saved custom images
  const [savedImages, setSavedImages] = useState([]);
  // State for saved export
  const [currentExport, setCurrentExport] = useState(null);
  // State for image gallery
  const [galleryVisible, setGalleryVisible] = useState(false);
  const [currentGalleryImage, setCurrentGalleryImage] = useState(null);
  const [currentGalleryIndex, setCurrentGalleryIndex] = useState(0);
  // Navigation state - use props if provided, otherwise use internal state
  const [activeSection, setInternalActiveSection] = useState(propActiveSection || 'dashboard');
  // Add these state variables after the existing state declarations
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  
  // Add cart context hook
  const { addToCart, removeFromCart, goToCheckout, cartItems: contextCartItems } = useCart();
  
  // Add stats state
  const [stats, setStats] = useState({
    users: 0,
    events: 0,
    templates: 0,
    uploads: 0
  });
  
  // Add latest events state for admin dashboard
  const [latestEvents, setLatestEvents] = useState([]);
  
  // Design limit state
  const [designsLimit, setDesignsLimit] = useState(20);
  const [limitOptions] = useState([20, 40, 'All']);
  const [totalDesignCount, setTotalDesignCount] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  // Add filter state
  const [designFilter, setDesignFilter] = useState('all');
  
  // Add shopping cart state
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  // Frame-package specific selections
  const [selectedFrameImageId, setSelectedFrameImageId] = useState(null);
  const [frameColor, setFrameColor] = useState('black');
  // Controls whether cart sidebar lists every package or only compatible ones
  const [showAllPackages, setShowAllPackages] = useState(false);
  const [isDownloadModalOpen, setDownloadModalOpen] = useState(false);
const [designToDownload , setDesignToDownload ] = useState(null);
const [downloadProcessing, setDownloadProcessing] = useState(false);
  // State for Frame info modal (needs to be before any early returns to keep hook order stable)
  const [isFrameInfoOpen, setFrameInfoOpen] = useState(false);
  const openFrameInfo = () => setFrameInfoOpen(true);
  const closeFrameInfo = () => setFrameInfoOpen(false);
  const [packageOptions] = useState([
    { id: 'basic', name: 'Basic', price: 30, imageCount: 1, description: '1 digital image' },
    { id: 'premium', name: 'Premium', price: 50, imageCount: 2, description: '2 digital images' },
    { id: 'ultimate', name: 'Ultimate', price: 65, imageCount: 3, description: '3 digital images' },
    { id: 'pro', name: 'Pro Bundle', price: 80, imageCount: 5, description: '5 digital images' },
    { id: 'frame', name: 'Frame Package', price: 100, imageCount: 3, description: '3 digital images + 14" x 18" Framed Print', includedFrames: 1 }
  ]);
  
  // Initialize toast
  const toast = useToast();
  
  const navigate = useNavigate();

  // Add ref for container
  const containerRef = useRef(null);
  
  // Add these state variables with the other state declarations
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileFormData, setProfileFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    id: ''
  });
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordFormData, setPasswordFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [profileUpdateLoading, setProfileUpdateLoading] = useState(false);
  const [passwordUpdateLoading, setPasswordUpdateLoading] = useState(false);
  
  // Track if swipe hint has been shown
  const [swipeHintShown, setSwipeHintShown] = useState(false);
  
  // AI Designs promotional banner state
  const [promoExpanded, setPromoExpanded] = useState(false);
  const [promoDismissed, setPromoDismissed] = useState(localStorage.getItem('ai_promo_dismissed') === 'true');
  
  // Check if tutorial should be shown
  useEffect(() => {
    const checkTutorialStatus = async () => {
      // Only show on dashboard or designs pages
      const isRelevantPath = location.pathname.includes('/dashboard') || 
                            location.pathname.includes('/designs') ||
                            activeSection === 'dashboard' || 
                            activeSection === 'designs';
      
      if (!isRelevantPath) return;
      
      try {
        // Check localStorage first - only hide if explicitly set
        const hideTutorial = localStorage.getItem('playmaker_hide_tutorial');
        if (hideTutorial === 'true') return;
        
        // Check user preferences in database if logged in - only hide if explicitly set
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const preferences = await getUserPreferences(user.id);
          
          if (preferences && preferences.hide_tutorial === true) {
            return;
          }
        }
        
        // If we got here, we should show the tutorial
        setShowTutorial(true);
      } catch (error) {
        console.error('Error checking tutorial status:', error);
      }
    };
    
    checkTutorialStatus();
  }, [location.pathname, activeSection]);
  
  // Show swipe hint only on mobile
  useEffect(() => {
    if (galleryVisible && savedImages.length > 1 && !swipeHintShown) {
      // Check if mobile
      const isMobile = window.innerWidth <= 768;
      if (isMobile) {
        setSwipeHintShown(true);
      }
    }
  }, [galleryVisible, savedImages.length, swipeHintShown]);
  
  // Reset swipe hint when gallery closes
  useEffect(() => {
    if (!galleryVisible) {
      // Reset hint after a delay
      const timer = setTimeout(() => {
        setSwipeHintShown(false);
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [galleryVisible]);
  
  // Update active section when prop changes
  useEffect(() => {
    if (propActiveSection) {
      setInternalActiveSection(propActiveSection);
    }
  }, [propActiveSection]);

  // Update selectSection function
  const selectSection = (section) => {
    setInternalActiveSection(section);
    
    // If parent component provided setActiveSection, call it too
    if (propSetActiveSection) {
      propSetActiveSection(section);
    }
  };

  // Listen for section-change events from Layout
  useEffect(() => {
    const handleSectionChangeEvent = (event) => {
      if (event.detail && event.detail.section) {
        setInternalActiveSection(event.detail.section);
      }
    };
    
    document.addEventListener('section-change', handleSectionChangeEvent);
    
    return () => {
      document.removeEventListener('section-change', handleSectionChangeEvent);
    };
  }, []);

  // CESDK editor ready handler
  const handleEditorReady = (cesdkInstance) => {
    setCesdk(cesdkInstance);
  };

  // Monitor CESDK initialization
  useEffect(() => {
    if (cesdk) {
      console.log("CESDK initialized successfully");
    }
  }, [cesdk]);

  // Check for impersonation on mount and URL changes
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const impersonating = params.get('impersonating') === 'true';
    const adminId = params.get('admin_id');
    
    console.log('Impersonation check:', { impersonating, adminId });
    
    // If we're impersonating, force admin to false
    if (impersonating) {
      setIsAdmin(false);
    }
    
    setIsImpersonating(impersonating);
    if (adminId) setOriginalAdminId(adminId);
    
    // If we're impersonating and on the admin route, redirect to dashboard
    if (impersonating && window.location.pathname.includes('/admin')) {
      navigate('/dashboard');
    }
  }, [location.search, navigate]); // Added navigate to dependencies

  // Fetch user session and data - update the admin check
  useEffect(() => {
    const fetchSessionAndData = async () => {
      const { data, error } = await supabase.auth.getSession();
      if (error || !data.session) {
        navigate('/login');
        return;
      }
      setUser(data.session.user);

      // Get the user's actual role from app_users
      const { data: userData, error: userError } = await supabase
        .from('app_users')
        .select('user_type, is_admin')
        .eq('id', data.session.user.id)
        .single();

      console.log('User data:', userData, 'Is impersonating:', isImpersonating);

      // When impersonating, force admin to false regardless of actual permissions
      if (!userError && userData) {
        const shouldBeAdmin = userData.user_type === 'staff' && userData.is_admin === true;
        // If we're impersonating, always set admin to false
        const finalAdminState = isImpersonating ? false : shouldBeAdmin;
        console.log('Setting admin state:', { shouldBeAdmin, isImpersonating, finalAdminState });
        setIsAdmin(finalAdminState);
        
        // If we're impersonating and on an admin-only route, redirect to dashboard
        if (isImpersonating && (
          window.location.pathname.includes('/admin') || 
          window.location.pathname.includes('/dashboard/events/')
        )) {
          navigate('/dashboard');
        }
      } else {
        setIsAdmin(false);
      }

      // Store impersonation state in user preferences if impersonating
      if (isImpersonating && originalAdminId) {
        try {
          const { error: prefError } = await supabase
            .from('user_preferences')
            .upsert({
              user_id: data.session.user.id,
              preferences: { 
                is_being_impersonated: true, 
                impersonator_id: originalAdminId,
                impersonation_start: new Date().toISOString()
              },
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            }, {
              onConflict: 'user_id',
              ignoreDuplicates: false
            });

          if (prefError) {
            console.error('Failed to store impersonation state:', prefError);
          }
        } catch (prefError) {
          console.error('Exception storing impersonation state:', prefError);
        }
      } else if (!isImpersonating) {
        // Clear impersonation state if not impersonating
        try {
          // First check if a preference record exists
          const { data: existingPref } = await supabase
            .from('user_preferences')
            .select('id')
            .eq('user_id', data.session.user.id)
            .single();

          if (existingPref) {
            // Update existing record
            const { error: prefError } = await supabase
              .from('user_preferences')
              .update({
                preferences: {},
                updated_at: new Date().toISOString()
              })
              .eq('user_id', data.session.user.id);

            if (prefError) {
              console.error('Failed to clear impersonation state:', prefError);
            }
          } else {
            // Insert new record
            const { error: prefError } = await supabase
              .from('user_preferences')
              .insert({
                user_id: data.session.user.id,
                preferences: {},
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              });

            if (prefError) {
              console.error('Failed to create user preferences:', prefError);
            }
          }
        } catch (prefError) {
          console.error('Exception handling user preferences:', prefError);
        }
      }

      // Fetch user uploads including watermarked images
      const { data: uploads, error: uploadError } = await supabase
        .from('user_uploads')
        .select('id, file_url, file_url_wm, is_primary')
        .eq('user_id', data.session.user.id)
        .eq('status', 'active');

      if (uploadError) {
        console.error('Error fetching user uploads:', uploadError);
        setError(uploadError.message);
      } else {
        // Get the active file URLs - use original URLs for matching purposes
        const activeFileUrls = uploads.map(upload => upload.file_url);
        
        // Find primary image - use watermarked version when available
        const primary = uploads.find(upload => upload.is_primary);
        setPrimaryImage(primary?.file_url_wm || primary?.file_url || null);

        // Create formatted images directly from user_uploads excluding placeholder picsum URLs
        const filteredUploads = uploads.filter(u => !u.file_url.includes('picsum.photos'));

        const formattedImages = filteredUploads.map(upload => ({
          name: upload.file_url.split('/').pop(), // Extract filename from URL
          url: upload.file_url_wm || upload.file_url, // Use watermarked version when available
          originalUrl: upload.file_url, // Keep original URL for reference
          event_id: null,
        }));

        setSourceImages(formattedImages);

        // If no primary image was set, use the first active image
        if (!primaryImage && formattedImages.length > 0) {
          setPrimaryImage(formattedImages[0].url);
        }
      }

      // Fetch templates
      const { data: templateData, error: templateError } = await supabase
        .from('templates')
        .select('id, name, thumbnail_url, placeholder_data, scene_url, style_category, style_subcategory')
        .order('updated_at', { ascending: false });
      if (templateError) {
        console.error('Error fetching templates:', templateError);
        setError(templateError.message);
      } else {
        setTemplates(templateData || []);
      }
      
      // First get the total count of designs
      const { count, error: countError } = await supabase
        .from('exports')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', data.session.user.id)
        .eq('status', 'active');
        
      if (countError) {
        console.error('Error counting exports:', countError);
      } else {
        setTotalDesignCount(count || 0);
      }
      
      // Then fetch only the initial limited set of designs
      await loadDesigns(designsLimit);
      
      // Fetch stats for admin view
      if (isAdmin) {
        try {
          // Fetch user count - with better error handling to prevent 500 errors
          let userCount = 0;
          try {
            const { count, error } = await supabase
                .from('app_users')
              .select('id', { count: 'exact', head: true });
            if (!error) userCount = count || 0;
          } catch (userError) {
            console.error('Unable to get user count:', userError);
          }
            
          // Fetch event count
            let eventCount = 0;
            try {
              const { count, error } = await supabase
                .from('events')
                .select('id', { count: 'exact', head: true });
              if (!error) eventCount = count || 0;
            } catch (eventError) {
              console.error('Unable to get event count:', eventError);
            }
            
            // Fetch upload count
            let uploadCount = 0;
            try {
              const { count, error } = await supabase
                .from('user_uploads')
                .select('id', { count: 'exact', head: true });
              if (!error) uploadCount = count || 0;
            } catch (uploadError) {
              console.error('Unable to get upload count:', uploadError);
            }
            
            setStats({
              users: userCount,
              events: eventCount,
              templates: templates.length,
              uploads: uploadCount
            });
        } catch (statsError) {
          console.error('Error fetching stats:', statsError);
        }
      }
    };
    
    fetchSessionAndData();
  }, [navigate, isImpersonating, originalAdminId]);

  // Add this function to load designs based on the selected limit
  const loadDesigns = async (limit, offset = 0) => {
    if (!user) return;
    
    try {
      setIsLoadingMore(true);
      
      // Determine how many to fetch
      const fetchLimit = limit === 'All' ? totalDesignCount || 1000 : limit;
      
      // First, fetch export records
      const { data: exportData, error: exportError } = await supabase
        .from('exports')
        .select('id, template_id, export_url, export_url_wm, exported_at, is_saved, is_purchased, display_priority, event_id')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .order('display_priority', { ascending: true, nullsLast: true })
        .order('exported_at', { ascending: false })
        .range(offset, offset + fetchLimit - 1); // Use range for pagination
        
      if (exportError) {
        console.error('Error fetching saved exports:', exportError);
        return;
      }
      
      // Map export data to include template name if available and properly handle watermarked vs original images
      const savedExports = exportData?.map(export_item => {
        const template = templates?.find(t => t.id === export_item.template_id);
        
        // Determine which URL to display based on purchase status
        // If the design is purchased, show the unwatermarked version (export_url)
        // Otherwise, show the watermarked version (export_url_wm if available)
        const displayUrl = export_item.is_purchased 
          ? export_item.export_url 
          : (export_item.export_url_wm || export_item.export_url);
        
        return {
          ...export_item,
          template_name: template ? template.name : 'Custom Design',
          display_url: displayUrl, // URL to display in the UI
          file_url: export_item.export_url, // Original unwatermarked file (for after purchase)
          file_url_wm: export_item.export_url_wm || export_item.export_url // Watermarked version
        };
      }) || [];
      
      // Filter out any exports with invalid URLs
      const validExports = savedExports.filter(item => 
        (item.display_url && !item.display_url.startsWith('blob:'))
      );
      
      // If this is a "load more" request (offset > 0), append to existing designs
      // Otherwise, replace the current list
      if (offset > 0) {
        setSavedImages(prevImages => [...prevImages, ...validExports]);
      } else {
        setSavedImages(validExports);
      }
    } catch (error) {
      console.error('Error loading designs:', error);
      setError('Failed to load designs');
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Add this useEffect to fetch more designs when limit changes
  useEffect(() => {
    if (user) {
      setSavedImages([]); // reset list when limit or event changes
      loadDesigns(designsLimit);
    }
  }, [designsLimit, user]);

  // Handle file upload with background removal
  const handleFileChange = async (event) => {
    if (!user) {
      setError('Please log in to upload an image');
      return;
    }

    setIsLoading(true);
    setError(null);
    const file = event.target.files[0];
    if (!file) {
      setIsLoading(false);
      return;
    }

    try {
      // Process with remove.bg API
      const formData = new FormData();
      formData.append('image_file', file);

      const response = await fetch('https://api.remove.bg/v1.0/removebg', {
        method: 'POST',
        headers: { 'X-API-Key': removeBgApiKey },
        body: formData,
      });

      if (!response.ok) throw new Error(`Remove.bg API error: ${await response.text()}`);

      // Create a blob from the response
      const blob = await response.blob();
      const processedFile = new File([blob], `processed-${Date.now()}.png`, { type: 'image/png' });

      // Upload to Supabase storage
      const filePath = `background_removed_images/${user.id}/${processedFile.name}`;
      const { error: uploadError } = await supabase.storage
        .from('playmakerpics-images')
        .upload(filePath, processedFile, { upsert: true });

      if (uploadError) throw uploadError;

      // Get public URL
      const { publicUrl } = supabase.storage
        .from('playmakerpics-images')
        .getPublicUrl(filePath).data;

      // Log upload and update state
      const isFirstImage = sourceImages.length === 0;
      await logUserUpload(user.id, publicUrl, isFirstImage);
      setUploadedImageURL(publicUrl);
      const newImage = { name: processedFile.name, url: publicUrl };
      setSourceImages([...sourceImages, newImage]);
      if (isFirstImage) setPrimaryImage(publicUrl);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Delete an image - update to use originalUrl for matching
  const handleDeleteImage = async (imageName, originalUrl) => {
    try {
      console.log('=== Starting Image Deletion Process ===');
      console.log('1. Initial Parameters:', { imageName, originalUrl, userId: user?.id });
      
      // Check if the upload exists in the database
      console.log('2. Checking if upload exists in database...');
      const { data: existingUpload, error: checkError } = await supabase
        .from('user_uploads')
        .select('*')
        .eq('user_id', user.id)
        .eq('file_url', originalUrl)
        .single();
                
      if (checkError) {
        console.error('❌ Error checking upload:', checkError);
        throw checkError;
      }

      if (!existingUpload) {
        console.error('❌ No matching upload found in database');
        throw new Error('No matching upload found in database');
      }

      console.log('3. Found existing upload:', existingUpload);

      // Update the status in the user_uploads table to 'deleted'
      console.log('4. Attempting database update...');
      const { data, error: dbError } = await supabase
        .from('user_uploads')
        .update({ status: 'deleted' })
        .eq('id', existingUpload.id)  // Use the exact ID we found
        .select();

      if (dbError) {
        console.error('❌ Database update failed:', dbError);
        throw dbError;
      }
      console.log('✅ Database update result:', data);

      // Update UI state by filtering out the deleted image
      console.log('5. Updating UI state...');
      const updatedImages = sourceImages.filter(img => img.originalUrl !== originalUrl);
      setSourceImages(updatedImages);
      console.log('Updated sourceImages count:', updatedImages.length);

      // If we deleted the primary image, update the primary image
      if (primaryImage === existingUpload.file_url_wm || primaryImage === existingUpload.file_url) {
        console.log('6. Primary image was deleted, updating primary image...');
        // Refetch the active uploads to get the most up-to-date data
        const { data: activeUploads } = await supabase
          .from('user_uploads')
          .select('id, file_url, file_url_wm, is_primary')
          .eq('user_id', user.id)
          .eq('status', 'active');
        
        console.log('Active uploads after deletion:', activeUploads);
        
        const newPrimary = activeUploads?.find(upload => upload.is_primary);
        if (newPrimary) {
          console.log('Setting new primary from database:', newPrimary.file_url_wm || newPrimary.file_url);
          setPrimaryImage(newPrimary.file_url_wm || newPrimary.file_url);
        } else if (updatedImages.length > 0) {
          console.log('No primary in DB, using first active image:', updatedImages[0].url);
          setPrimaryImage(updatedImages[0].url);
        } else {
          console.log('No images remaining, setting primary to null');
          setPrimaryImage(null);
        }
      }

      console.log('=== Image Deletion Process Completed Successfully ===');
    } catch (err) {
      console.error('❌ Error in handleDeleteImage:', err);
      setError(err.message);
    }
  };

  // Set primary image - update to handle both file_url and file_url_wm
  const handleSetPrimary = async (url, originalUrl) => {
    try {
      setPrimaryImage(url);
      await supabase
        .from('user_uploads')
        .update({ is_primary: false })
        .eq('user_id', user.id);
      await supabase
        .from('user_uploads')
        .update({ is_primary: true })
        .eq('user_id', user.id)
        .eq('file_url', originalUrl);
    } catch (err) {
      console.error('Error setting primary image:', err);
      setError(err.message);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  // Handle template customization
  const handleCustomizeTemplate = async (template) => {
    trackEvent('template_customize', {
      templateId: template?.id || null,
      userId: user?.id || null,
    });
    if (!cesdk) {
      setError("Editor not initialized. Please refresh the page and try again.");
            return;
    }
    
    setSelectedTemplate(template);
    setIsLoading(true);
    
    try {
      // Load the template scene into the hidden editor to analyze it
      if (!template.scene_url) {
        throw new Error("Template has no scene URL");
      }
      
      // Fetch scene data
      const response = await fetch(template.scene_url);
      if (!response.ok) {
        throw new Error(`Failed to fetch scene: ${response.statusText}`);
      }
      
      const sceneText = await response.text();
      
      // Load the scene - try different methods for compatibility
      let loadSuccess = false;
      
      // Method 1: Direct loadFromString
      if (!loadSuccess && typeof cesdk.loadFromString === 'function') {
        try {
          await cesdk.loadFromString(sceneText);
          loadSuccess = true;
        } catch (err) {
          console.warn("Failed loading with direct method:", err.message);
        }
      }
      
      // Method 2: Engine scene loadFromString
      if (!loadSuccess && cesdk.engine && cesdk.engine.scene && 
          typeof cesdk.engine.scene.loadFromString === 'function') {
        try {
          await cesdk.engine.scene.loadFromString(sceneText);
          loadSuccess = true;
        } catch (err) {
          console.warn("Failed loading with engine method:", err.message);
        }
      }
      
      if (!loadSuccess) {
        throw new Error("Could not load template for analysis. Please try again later.");
      }
      
      // Find all blocks in the scene
      const scene = cesdk.engine.scene.get();
      if (!scene) {
        throw new Error("No scene is loaded");
      }
      
      // Get all pages in the scene
      const pages = cesdk.engine.block.getChildren(scene);
      
      // Get all blocks from all pages including nested blocks
      const getAllBlocks = () => {
        const allBlocks = [];
        
        const processBlock = (blockId) => {
          allBlocks.push(blockId);
          
          try {
            // Try to get children if this block has any
            const children = cesdk.engine.block.getChildren(blockId);
            if (children && children.length > 0) {
              children.forEach(childId => processBlock(childId));
            }
          } catch (e) {
            // Ignore errors for blocks that don't have children
          }
        };
        
        pages.forEach(pageId => processBlock(pageId));
        return allBlocks;
      };
      
      const allBlocks = getAllBlocks();
      console.log(`Found ${allBlocks.length} total blocks in template`);
      
      // Find text blocks that appear to be placeholders
      const textBlocks = allBlocks.filter(id => {
        try {
          return cesdk.engine.block.getType(id) === "//ly.img.ubq/text";
        } catch (e) {
          return false;
        }
      });
      
      console.log(`Found ${textBlocks.length} text blocks in template`);
      
      // Identify placeholder fields by analyzing text content and block names
      const fields = [];
      const detectedFields = new Set(); // To avoid duplicates
      
      textBlocks.forEach(id => {
        try {
          const blockName = cesdk.engine.block.getName(id) || "";
          const text = cesdk.engine.block.getString(id, "text/text") || "";
          
          console.log(`Text block: "${blockName}", content: "${text}"`);
          
          // Check if this block appears to be a placeholder
          const isPlaceholder = 
            // All uppercase text is usually a placeholder
            (text === text.toUpperCase() && text.length > 0 && text.length < 30) ||
            // Or block names that look like field names
            (/TITLE|NAME|LAST|FIRST|JERSEY|NUMBER/i.test(blockName)) ||
            // Special case for title blocks
            (blockName === "TITLE" || blockName === "Title" || text === "TITLE");
          
          if (isPlaceholder) {
            // Determine field name and type based on block name and content
            let fieldName = "";
            
            // Try to determine a good field name
            if (/JERSEY|JERSEY_NUMBER|NUMBER/i.test(blockName) || /^[0-9]+$/.test(text) || text === "JERSEY NUMBER") {
              fieldName = "JERSEY NUMBER";
            } else if (/LAST|LASTNAME|LAST_NAME|SURNAME/i.test(blockName) || text === "LAST NAMER") {
              fieldName = "LAST NAMER";
            } else if (/FIRST|FIRSTNAME|FIRST_NAME/i.test(blockName)) {
              fieldName = "FIRST NAME";
            } else if (/TITLE/i.test(blockName) || text === "TITLE") {
              fieldName = "TITLE";
            } else if (/NAME/i.test(blockName) || text === "NAME") {
              fieldName = "NAME";
          } else {
              // Use the text content as field name if it's all uppercase
              fieldName = text === text.toUpperCase() ? text : blockName;
            }
            
            // Only add if we haven't seen this field before
            if (fieldName && !detectedFields.has(fieldName)) {
              fields.push({
                name: fieldName,
                type: 'text',
                originalText: text,
                blockName: blockName,
                blockId: id
              });
              detectedFields.add(fieldName);
            }
          }
        } catch (e) {
          console.warn(`Error analyzing text block:`, e);
        }
      });
      
      // If no text fields were found, add a default NAME field
      if (fields.length === 0 || !fields.some(f => f.name === "NAME" || f.name === "LAST NAMER")) {
        fields.push({
          name: "NAME",
          type: "text",
          originalText: "",
          blockName: "",
          blockId: null
        });
      }
      
      console.log("Detected form fields:", fields);
      
      // Add ATHLETE IMAGE as a special field
      fields.push({
        name: "ATHLETE IMAGE",
        type: "image",
        originalText: "",
        blockName: "ATHLETE",
        blockId: null
      });
      
      setPlaceholderFields(fields);
      
      // Initialize form data with empty values
      const initialFormData = {};
      fields.forEach(field => {
        if (field.type === 'text') {
          initialFormData[field.name] = '';
        }
      });
      
      setFormData(initialFormData);
      
      // Set modal title to template name
      // No need to update placeholder data since we're using the fields array directly
      
    } catch (err) {
      console.error("Error analyzing template:", err);
      setError("Error analyzing template: " + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  // Generate preview of the customized template
  const handleGeneratePreview = async () => {
    if (!cesdk || !selectedTemplate) {
      setError("Editor not ready or no template selected");
          return;
        }
        
    // Check if we have a primary image
    if (!primaryImage) {
      setError("Please select a primary image first.");
      return;
    }
    
    setIsGeneratingPreview(true);
    setError(null);
    setExportedImage(null);
    
    try {
      // Load the template scene into the hidden editor
      if (!selectedTemplate.scene_url) {
        throw new Error("Template has no scene URL");
      }
      
      // Fetch scene data
      const response = await fetch(selectedTemplate.scene_url);
      if (!response.ok) {
        throw new Error(`Failed to fetch scene: ${response.statusText}`);
      }
      
      const sceneText = await response.text();
      
      // Load the scene - try different methods for compatibility
      let loadSuccess = false;
      
      // Method 1: Direct loadFromString
      if (!loadSuccess && typeof cesdk.loadFromString === 'function') {
        try {
          await cesdk.loadFromString(sceneText);
          loadSuccess = true;
        } catch (err) {
          console.warn("Failed loading with direct method:", err.message);
        }
      }
      
      // Method 2: Engine scene loadFromString
      if (!loadSuccess && cesdk.engine && cesdk.engine.scene && 
          typeof cesdk.engine.scene.loadFromString === 'function') {
        try {
          await cesdk.engine.scene.loadFromString(sceneText);
          loadSuccess = true;
        } catch (err) {
          console.warn("Failed loading with engine method:", err.message);
        }
      }
      
      if (!loadSuccess) {
        throw new Error("Could not load template. Please try again later.");
      }
      
      // Create a method to apply customizations to the scene
      const applyCustomizations = async () => {
        console.log("Applying customizations with form data:", formData);
        
        if (!cesdk.engine || !cesdk.engine.scene) {
          throw new Error("Scene API is not available");
        }
        
        const scene = cesdk.engine.scene.get();
        if (!scene) {
          throw new Error("No scene is loaded");
        }
        
        try {
          // Get all pages in the scene
          const pages = cesdk.engine.block.getChildren(scene);
          
          // Get all blocks from all pages
          const allBlocks = [];
          for (const pageId of pages) {
            try {
              const pageBlocks = cesdk.engine.block.getChildren(pageId);
              allBlocks.push(...pageBlocks);
            } catch (e) {
              console.warn(`Could not get children of page ${pageId}:`, e);
            }
          }
          
          // Get grandchildren blocks since text might be inside deeper blocks
          const allBlocksWithChildren = [...allBlocks];
          
          // Add grandchildren and great-grandchildren to ensure we catch all text blocks
          for (const blockId of allBlocks) {
            try {
              if (cesdk.engine.block.getType(blockId) !== "//ly.img.ubq/text") {
                const childrenBlocks = cesdk.engine.block.getChildren(blockId);
                
                // Add immediate children
                allBlocksWithChildren.push(...childrenBlocks);
                
                // And try to get their children too (for deeply nested blocks)
                for (const childId of childrenBlocks) {
                  try {
                    const grandchildren = cesdk.engine.block.getChildren(childId);
                    allBlocksWithChildren.push(...grandchildren);
                  } catch (e) {
                    // Ignore errors on grandchildren
                  }
                }
              }
            } catch (e) {
              // Ignore errors on getting children
            }
          }
          
          console.log(`Found ${allBlocksWithChildren.length} total blocks (including nested blocks)`);
          
          // Log all text blocks before we try to modify them
          const textBlocks = allBlocksWithChildren.filter(id => {
            try {
              return cesdk.engine.block.getType(id) === "//ly.img.ubq/text";
            } catch (e) {
              return false;
            }
          });
          
          console.log(`Found ${textBlocks.length} text blocks to process:`);
          textBlocks.forEach(id => {
            try {
              const name = cesdk.engine.block.getName(id) || "unnamed";
              const text = cesdk.engine.block.getString(id, "text/text");
              console.log(`- Text block: "${name}", content: "${text}"`);
            } catch (e) {
              console.log(`- Error getting text block info: ${e.message}`);
            }
          });
          
          // Apply customizations to text blocks and placeholders
          for (const blockId of allBlocksWithChildren) {
            try {
              const blockType = cesdk.engine.block.getType(blockId);
              const blockName = cesdk.engine.block.getName(blockId);
              
              // Set text for any text blocks
              if (blockType === "//ly.img.ubq/text") {
                try {
                  console.log(`Processing text block: ${blockName || "unnamed"}, ID: ${blockId}`);
                  
                  // Get current text to see if we need to replace it
                  const currentText = cesdk.engine.block.getString(blockId, "text/text");
                  console.log(`Current text: "${currentText}"`);
                  
                  // Go through all the text fields and see if this block matches any of them
                  let handled = false;
                  
                  // First check if this block directly matches one of our detected fields
                  placeholderFields.forEach(field => {
                    if (field.type === 'text' && field.blockId === blockId && formData[field.name]) {
                      console.log(`Directly replacing text in known block ${field.name}: "${currentText}" with "${formData[field.name]}"`);
                      cesdk.engine.block.setString(blockId, "text/text", formData[field.name]);
                      handled = true;
                    }
                  });
                  
                  // If not directly matched, try to match by name/content patterns
                  if (!handled) {
                    // Try to match by content or block name
                    for (const [fieldName, value] of Object.entries(formData)) {
                      if (!value) continue; // Skip empty values
                      
                      // Check various text matching strategies
                      if (
                        // Exact field name match with block name
                        (blockName && blockName.toUpperCase() === fieldName) ||
                        // Exact field name match with text content
                        (currentText && currentText === fieldName) ||
                        // For JERSEY NUMBER, check if it's a number field or contains NUMBER
                        (fieldName === "JERSEY NUMBER" && (/^[0-9]+$/.test(currentText) || /NUMBER/i.test(blockName) || /JERSEY/i.test(blockName))) ||
                        // For LAST NAMER, check if it contains LAST or NAMER
                        (fieldName === "LAST NAMER" && (/LAST/i.test(blockName) || /NAMER/i.test(blockName) || currentText === "LAST NAMER")) ||
                        // General pattern: all uppercase content
                        (currentText === currentText.toUpperCase() && currentText.length > 0 && currentText.length < 30)
                      ) {
                        console.log(`Replacing text via pattern matching for ${fieldName}: "${currentText}" with "${value}"`);
                        cesdk.engine.block.setString(blockId, "text/text", value);
                        handled = true;
                        break;
                      }
                    }
                  }
                  
                  // If field wasn't handled by specific fields, try to use NAME as fallback
                  if (!handled && formData["NAME"]) {
                    if (
                      (blockName === "TITLE" || blockName === "Title" || currentText === "TITLE") ||
                      (currentText && currentText === currentText.toUpperCase() && currentText.length < 30 && !/[a-z]/.test(currentText))
                    ) {
                      console.log(`Fallback: replacing with NAME field value: "${formData["NAME"]}"`);
                      cesdk.engine.block.setString(blockId, "text/text", formData["NAME"]);
                    }
                  }
                } catch (textErr) {
                  console.warn(`Error processing text block ${blockName || 'unnamed'}:`, textErr);
                }
              }
              
              // Apply primary image to placeholder graphics
              if (blockType === "//ly.img.ubq/graphic") {
                // Look for various indicators that this is a placeholder block
                const isPlaceholder = blockName === "Person" || 
                                      blockName === "Athlete" || 
                                      blockName === "ATHLETE" ||
                                      blockName === "PLAYER" ||
                                      blockName === "Player" || 
                                      /athlete|player|person|placeholder/i.test(blockName);
                
                // Check for placeholder metadata as a fallback
                let hasPlaceholderMetadata = false;
                try {
                  const placeholderMeta = cesdk.engine.block.getMetadata(blockId, "isPlaceholder", "false");
                  hasPlaceholderMetadata = placeholderMeta === "true";
                } catch (e) {
                  // Ignore errors checking metadata
                }
                
                if (isPlaceholder || hasPlaceholderMetadata) {
                  console.log(`Found athlete/person placeholder: ${blockName}`);
                  if (primaryImage) {
                    // Try different methods to set the image
                    try {
                      // 1. Check for fill
                      const fillId = cesdk.engine.block.getFill(blockId);
                      if (fillId && cesdk.engine.block.isValid(fillId)) {
                        const fillType = cesdk.engine.block.getType(fillId);
                        
                        // If it's an image fill, set it
                        if (fillType === "//ly.img.ubq/fill/image") {
                          try {
                            cesdk.engine.block.setSourceSet(fillId, "fill/image/sourceSet", [
                              {
                                uri: primaryImage,
                                width: 1080,
                                height: 720,
                              },
                            ]);
                          } catch (e) {
                            // Try the older method
                            cesdk.engine.block.setString(fillId, "fill/image/imageFileURI", primaryImage);
                          }
                        } else {
                          // If it's not an image fill, create a new one
                          const newFillId = cesdk.engine.block.createFill("image");
                          try {
                            cesdk.engine.block.setSourceSet(newFillId, "fill/image/sourceSet", [
                              {
                                uri: primaryImage,
                                width: 1080,
                                height: 720,
                              },
                            ]);
                          } catch (e) {
                            cesdk.engine.block.setString(newFillId, "fill/image/imageFileURI", primaryImage);
                          }
                          cesdk.engine.block.setFill(blockId, newFillId);
                        }
                      }
                    } catch (e) {
                      console.warn("Error setting image via fill:", e);
                    }
                  }
                }
              }
            } catch (err) {
              console.warn(`Error processing block:`, err);
            }
          }
        } catch (err) {
          console.error("Error in applying customizations:", err);
          throw err;
        }
      };
      
      // Apply customizations
      await applyCustomizations();
      
      // Export the customized scene as an image
      const scene = cesdk.engine.scene.get();
      const originalBlob = await cesdk.engine.block.export(scene, "image/png", { 
        dpi: 300,
        transparency: true
      });
      
      // Add watermark to the exported image
      let processedBlob;
      
      // Choose between text watermark and logo watermark
      // Option 1: Text watermark
      processedBlob = await addWatermark(originalBlob, {
        text: "PlayMaker Pics", 
        color: "rgba(255, 255, 255, 0.6)",
        font: "24px Arial",
        position: 9 // Bottom right
      });
      
      // Option 2: If you want to use a logo instead - comment out the above and uncomment this
      // const logoUrl = '/path/to/your/logo.png'; // Update with your logo path
      // processedBlob = await addLogoWatermark(originalBlob, logoUrl, {
      //   size: 15, // 15% of shorter dimension
      //   opacity: 0.6,
      //   position: 9, // Bottom right
      //   margin: 20
      // });
      
      // Create object URL for preview (just for display)
      const localObjectUrl = URL.createObjectURL(processedBlob);
      
      // Upload to Supabase storage for permanent storage
      const exportFileName = `export-${selectedTemplate.id}-${Date.now()}.png`;
      let supabaseUrl = null;
      
      try {
        // Upload the watermarked blob to Supabase
        const { data, error: uploadError } = await supabase.storage
          .from("playmakerpics-images")
          .upload(`exports/preview/${user.id}/${exportFileName}`, processedBlob, { 
            contentType: "image/png",
            upsert: true
          });
        
        if (uploadError) {
          console.warn("Warning: Supabase upload failed, using local object URL:", uploadError);
        } else {
          // Get the public URL
          supabaseUrl = supabase.storage
            .from("playmakerpics-images")
            .getPublicUrl(`exports/preview/${user.id}/${exportFileName}`).data.publicUrl;
          
          console.log("Successfully uploaded preview to Supabase:", supabaseUrl);
        }
      } catch (uploadError) {
        console.warn("Error uploading to Supabase:", uploadError);
        // Continue with local URL if upload fails
      }
      
      // Set the exportedImage to the Supabase URL if available, otherwise use the local URL
      setExportedImage({
        localUrl: localObjectUrl,  // For display in the UI
        supabaseUrl: supabaseUrl,  // For saving to database
        fileName: exportFileName   // For download functionality
      });
      
    } catch (err) {
      console.error("Preview generation error:", err);
      setError("Preview generation failed: " + err.message);
    } finally {
      setIsGeneratingPreview(false);
    }
  };

  // Save the customized template as a design
  const handleSaveCustomized = async () => {
    if (!exportedImage || !selectedTemplate) {
      setError("No preview to save. Please generate a preview first.");
      return;
    }
    
    setIsLoading(true);
    
    try {
      let finalUrl = exportedImage.supabaseUrl;
      
      // If we don't have a Supabase URL already, upload the local blob
      if (!finalUrl && exportedImage.localUrl) {
        // Convert the local URL to a blob
        const response = await fetch(exportedImage.localUrl);
        const originalBlob = await response.blob();
        
        // Add watermark to the blob if it doesn't already have one
        // (Preview images already have watermarks, but we're being thorough)
        const processedBlob = await addWatermark(originalBlob, {
          text: "PlayMaker Pics", 
          color: "rgba(255, 255, 255, 0.6)",
          font: "24px Arial",
          position: 9 // Bottom right
        });
        
        // Use existing fileName from exportedImage or generate a new one
        const fileName = exportedImage.fileName || `export-${selectedTemplate.id}-${Date.now()}.png`;
        
        const { error: uploadError } = await supabase.storage
          .from("playmakerpics-images")
          .upload(`exports/${user.id}/${fileName}`, processedBlob, { 
            contentType: "image/png",
            upsert: true
          });
        
        if (uploadError) throw uploadError;
        
        // Get public URL
        finalUrl = supabase.storage
          .from("playmakerpics-images")
          .getPublicUrl(`exports/${user.id}/${fileName}`).data.publicUrl;
      }
      
      // If we still don't have a URL, something is wrong
      if (!finalUrl) {
        throw new Error("Failed to get a valid URL for the image");
      }
      
      // Log export in database as saved JB added the event_id and hopefully the selectedEvent.id is correct
      const { data: exportRecord, error: exportError } = await supabase.from("exports").insert({
        user_id: user.id,
        template_id: selectedTemplate.id,
        export_url: finalUrl,
        export_url_wm: finalWatermarkedUrl,
        exported_at: new Date().toISOString(),
        is_saved: true,

        event_id: selectedTemplate.event_id || null,
        source_upload_id: primaryImageObject?.id || null,
        placeholder_values: formData          // ← jsonb
      }).select().single();
      
      if (exportError) throw exportError;
      
      // Add to saved images state
      const newSavedImage = {
        ...exportRecord,
        template_name: selectedTemplate.name
      };
      
      setSavedImages(prev => [newSavedImage, ...prev]);
      
      // Show success message and close modal
      setError(null);
      alert("Design saved successfully!");
      setSelectedTemplate(null);
      setExportedImage(null);
      
    } catch (err) {
      console.error("Error saving design:", err);
      setError("Failed to save design: " + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Add this new function for deleting saved designs
  const handleDeleteDesign = async (designId, exportUrl) => {
    // Show confirmation dialog first
    if (!window.confirm("Are you sure you want to delete this design? This action cannot be undone.")) {
      return; // User canceled the deletion
    }
    
    try {
      setIsLoading(true);
      
      // 1. First delete from exports table
      const { error: dbError } = await supabase
        .from('exports')
        .update({ status: 'deleted' })
        .eq('id', designId);
      
      if (dbError) {
        throw new Error(`Failed to delete design from database: ${dbError.message}`);
      }
      
      // 2. Try to delete the file from storage (this might fail if path is unclear)
      try {
        // Parse the URL to get the path
        const urlObj = new URL(exportUrl);
        const pathMatch = urlObj.pathname.match(/\/object\/public\/playmakerpics-images\/(.+?)(\?|$)/);
        
        if (pathMatch && pathMatch[1]) {
          const storagePath = decodeURIComponent(pathMatch[1]);
          console.log("Attempting to delete file at path:", storagePath);
          
          // Delete from storage
          const { error: storageError } = await supabase.storage
            .from('playmakerpics-images')
            .remove([storagePath]);
          
          if (storageError) {
            console.warn("Could not delete file from storage:", storageError);
          }
        }
      } catch (storageError) {
        console.warn("Error parsing or deleting file from storage:", storageError);
        // Continue anyway as the database record is more important
      }
      
      // 3. Refresh designs list to ensure UI sync
      await loadDesigns(designsLimit);
      
      toast({
        title: "Design deleted",
        description: "The design has been removed from your account",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      
    } catch (err) {
      console.error('Error deleting design:', err);
      setError(err.message);
      
      toast({
        title: "Error deleting design",
        description: err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Update the componentWillUnmount to clean up any body styles
  useEffect(() => {
    // Cleanup function to reset body overflow when component unmounts
    return () => {
      document.body.classList.remove('menu-open');
    };
  }, []);

  // Update the useEffect that handles route changes
  useEffect(() => {
    // Check if we're on the editor page and apply appropriate styling
    const path = window.location.pathname;
    const isEditorPage = path.includes('/editor');
    
    // Set data attribute on body for CSS targeting
    if (isEditorPage) {
      document.body.setAttribute('data-page', 'editor');
    } else {
      document.body.setAttribute('data-page', 'dashboard');
    }
    
    // Don't modify sidebar visibility - let AppNavigation handle it
    // This code previously tried to show the internal sidebar which conflicts with AppNavigation
    
    // Set an event listener to check route changes
    const handleRouteChange = () => {
      const currentPath = window.location.pathname;
      const isCurrentEditorPage = currentPath.includes('/editor');
      
      // Update body data attribute on route change
      if (isCurrentEditorPage) {
        document.body.setAttribute('data-page', 'editor');
      } else {
        document.body.setAttribute('data-page', 'dashboard');
      }
      
      // Don't modify sidebar visibility here either
    };
    
    window.addEventListener('popstate', handleRouteChange);
    
    return () => {
      window.removeEventListener('popstate', handleRouteChange);
      // Reset body attribute when component unmounts
      document.body.removeAttribute('data-page');
    };
  }, []);

  // Add this useEffect for better scrolling specifically in the admin section
  useEffect(() => {
    const adminSection = document.querySelector('.admin-dashboard-view');
    if (adminSection && activeSection === 'admin') {
      // Fix iOS scrolling issues in the admin section
      const touchStartHandler = (e) => {
        // If we're in the admin section, ensure scrolling works properly
        if (e.touches.length === 1) {
          // Allow default scrolling behavior
          e.stopPropagation();
        }
      };
      
      adminSection.addEventListener('touchstart', touchStartHandler, { passive: true });
      
      // Make sure the admin section is scrollable
      adminSection.style.overflow = 'auto';
      adminSection.style.WebkitOverflowScrolling = 'touch';
      
      return () => {
        adminSection.removeEventListener('touchstart', touchStartHandler);
      };
    }
  }, [activeSection]);

  // Add these handler functions before the return statement
  const handleEditProfile = () => {
    setProfileFormData({
      firstName: user?.user_metadata?.given_name || '',
      lastName: user?.user_metadata?.family_name || '',
      email: user?.email || '',
      id: user?.id || ''
    });
    setIsEditingProfile(true);
  };

  const handleProfileInputChange = (e) => {
    const { name, value } = e.target;
    setProfileFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePasswordInputChange = (e) => {
    const { name, value } = e.target;
    setPasswordFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setProfileUpdateLoading(true);
    
    try {
      const result = await updateUserProfile({
        firstName: profileFormData.firstName,
        lastName: profileFormData.lastName,
        id: user.id,
        avatarUrl: user?.user_metadata?.avatar_url || '',
        provider: user?.app_metadata?.provider || 'email'
      });
      
      // Update local user state to reflect changes
      const { data: { user: updatedUser }, error: userError } = await supabase.auth.getUser();
      if (!userError) {
        setUser(updatedUser);
      }
      
      setIsEditingProfile(false);
      toast({
        title: "Profile updated",
        description: "Your profile has been updated successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      } catch (error) {
      console.error('Error updating profile:', error);
        toast({
        title: "Update failed",
          description: error.message,
        status: "error",
          duration: 5000,
          isClosable: true,
        });
    } finally {
      setProfileUpdateLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    // Validate passwords
    if (passwordFormData.newPassword !== passwordFormData.confirmPassword) {
      toast({
        title: "Passwords don't match",
        description: "New password and confirmation must match",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    if (passwordFormData.newPassword.length < 6) {
      toast({
        title: "Password too short",
        description: "Password must be at least 6 characters",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    setPasswordUpdateLoading(true);
    
    try {
      await changeUserPassword(
        passwordFormData.currentPassword,
        passwordFormData.newPassword
      );
      
      setIsChangingPassword(false);
      setPasswordFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
      toast({
        title: "Password updated",
        description: "Your password has been changed successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error changing password:', error);
      toast({
        title: "Password change failed",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setPasswordUpdateLoading(false);
    }
  };

  // Add this useEffect for masonry grid layout
  useEffect(() => {
    if (activeSection === 'designs' && savedImages.length > 0) {
      // Small delay to ensure images have time to load their dimensions
      const timer = setTimeout(() => {
        const items = document.querySelectorAll('.clean-design');
        items.forEach(item => {
          const img = item.querySelector('img');
          if (img && !img.dataset.sized) {
            img.onload = () => {
              const height = img.naturalHeight;
              const rowSpan = Math.ceil(height / 10);
              item.style.setProperty('--row-span', rowSpan);
              img.dataset.sized = 'true';
            };
            
            if (img.complete) {
              const height = img.naturalHeight || 300;
              const rowSpan = Math.ceil(height / 10);
              item.style.setProperty('--row-span', rowSpan);
              img.dataset.sized = 'true';
            }
          }
        });
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [activeSection, savedImages]);

  // Function to handle gallery image selection
  const openGalleryImage = (design, index) => {
    trackEvent('design_view', {
      designId: design?.id || null,
      index,
      userId: user?.id || null,
    });
    // Check if it's a user image or design and format accordingly
    if (design.url) {
      // It's from sourceImages (user upload)
      setCurrentGalleryImage({
        export_url: design.url,
        id: design.name,
        template_name: "User Image"
      });
    } else {
      // It's from savedImages (design)
      setCurrentGalleryImage(design);
    }
    
    setCurrentGalleryIndex(index);
    setGalleryVisible(true);
  };

  // Add useEffect for keyboard navigation in gallery
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!galleryVisible) return;
      
      // Determine which image array to use based on active section
      const imagesArray = activeSection === 'images' ? sourceImages : savedImages;
      
      // Left arrow key
      if (e.key === 'ArrowLeft' && imagesArray.length > 1) {
        const prevIndex = (currentGalleryIndex - 1 + imagesArray.length) % imagesArray.length;
        setCurrentGalleryIndex(prevIndex);
        
        if (activeSection === 'images') {
          // For Media Day Pics
          setCurrentGalleryImage({
            export_url: imagesArray[prevIndex].url,
            id: imagesArray[prevIndex].name,
            template_name: "User Image"
          });
        } else {
          // For Designs
          setCurrentGalleryImage(imagesArray[prevIndex]);
        }
      }
      
      // Right arrow key
      if (e.key === 'ArrowRight' && imagesArray.length > 1) {
        const nextIndex = (currentGalleryIndex + 1) % imagesArray.length;
        setCurrentGalleryIndex(nextIndex);
        
        if (activeSection === 'images') {
          // For Media Day Pics
          setCurrentGalleryImage({
            export_url: imagesArray[nextIndex].url,
            id: imagesArray[nextIndex].name,
            template_name: "User Image"
          });
        } else {
          // For Designs
          setCurrentGalleryImage(imagesArray[nextIndex]);
        }
      }
      
      // Escape key
      if (e.key === 'Escape') {
        setGalleryVisible(false);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [galleryVisible, currentGalleryIndex, savedImages, sourceImages, activeSection]);
  
  // Touch swipe functionality references
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);
  
  // Touch handlers for mobile swipe
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  
  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };
  
  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    
    const diffX = touchStartX.current - touchEndX.current;
    const threshold = 50; // Min swipe distance
    
    // Determine which image array to use based on active section
    const imagesArray = activeSection === 'images' ? sourceImages : savedImages;
    
    if (Math.abs(diffX) > threshold && imagesArray.length > 1) {
      if (diffX > 0) {
        // Swiped left, go to next
        const nextIndex = (currentGalleryIndex + 1) % imagesArray.length;
        setCurrentGalleryIndex(nextIndex);
        
        if (activeSection === 'images') {
          // For Media Day Pics
          setCurrentGalleryImage({
            export_url: imagesArray[nextIndex].url,
            id: imagesArray[nextIndex].name,
            template_name: "User Image"
          });
        } else {
          // For Designs
          setCurrentGalleryImage(imagesArray[nextIndex]);
        }
      } else {
        // Swiped right, go to previous
        const prevIndex = (currentGalleryIndex - 1 + imagesArray.length) % imagesArray.length;
        setCurrentGalleryIndex(prevIndex);
        
        if (activeSection === 'images') {
          // For Media Day Pics
          setCurrentGalleryImage({
            export_url: imagesArray[prevIndex].url,
            id: imagesArray[prevIndex].name,
            template_name: "User Image"
          });
        } else {
          // For Designs
          setCurrentGalleryImage(imagesArray[prevIndex]);
        }
      }
    }
    
    // Reset
    touchStartX.current = null;
    touchEndX.current = null;
  };

  // Handle mobile fullscreen experience
  useEffect(() => {
    // Only run on mobile devices
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    if (galleryVisible && isMobile) {
      // Prevent scrolling on the background when gallery is open
      document.body.style.overflow = 'hidden';
      
      // Lock to fullscreen if possible (works on some browsers)
      try {
        if (document.documentElement.requestFullscreen && !document.fullscreenElement) {
          document.documentElement.requestFullscreen().catch(err => {
            console.log('Error attempting to enable fullscreen:', err);
          });
        }
      } catch (error) {
        console.log('Fullscreen API not supported');
      }
      
      // Listen for orientation changes to adjust layout
      const handleOrientationChange = () => {
        // Force redraw of the gallery to fit new dimensions
        const galleryContainer = document.querySelector('.gallery-image-container');
        if (galleryContainer) {
          // Small delay to let the browser complete the rotation
          setTimeout(() => {
            galleryContainer.style.height = window.innerHeight + 'px';
            
            // Force reflow
            galleryContainer.style.display = 'none';
            void galleryContainer.offsetHeight; // Trigger reflow
            galleryContainer.style.display = 'flex';
          }, 300);
        }
      };
      
      window.addEventListener('orientationchange', handleOrientationChange);
      
      return () => {
        // Cleanup
        document.body.style.overflow = '';
        window.removeEventListener('orientationchange', handleOrientationChange);
        
        // Exit fullscreen when closing gallery
        if (document.fullscreenElement) {
          document.exitFullscreen().catch(err => {
            console.log('Error exiting fullscreen:', err);
          });
        }
      };
    }
  }, [galleryVisible]);

  // Fetch stats for admin view
  useEffect(() => {
    const fetchAdminData = async () => {
      if (!user || !isAdmin) return;
      if (activeSection !== 'dashboard') return;
      
      try {
        console.log('Admin dashboard: Fetching latest events...');
        
        // Updated query to match the actual database schema
        const { data: eventsData, error: eventsError } = await supabase
          .from('events')
          .select('id, name, created_at, start_date, end_date, status, address, city, state')
          .order('created_at', { ascending: false })
          .limit(5);
          
        if (eventsError) {
          console.error('Error fetching latest events:', eventsError);
          setLatestEvents([]);
        } else {
          console.log('Successfully fetched events:', eventsData);
          setLatestEvents(eventsData || []);
        }
      } catch (error) {
        console.error('Error fetching admin dashboard data:', error);
        setLatestEvents([]);
      }
    };
    
    fetchAdminData();
  }, [isAdmin, user, activeSection]);

  // Add this new function to get the appropriate package based on cart items
  const getRecommendedPackage = () => {
    const itemCount = contextCartItems.length;
    if (itemCount === 0) return null;
    if (itemCount === 1) return packageOptions[0]; // Basic
    if (itemCount === 2) return packageOptions[1]; // Premium
    if (itemCount === 3) return packageOptions.find(p=>p.id==='ultimate'); // Ultimate
    if (itemCount === 5) return packageOptions.find(p=>p.id==='pro');     // Pro Bundle for 5 items
    return { ...packageOptions.find(p=>p.id==='ultimate'), price: 65 + ((itemCount - 3) * 20) }; // Custom pricing for other counts >3
  };

  // Add this function to handle adding designs to cart
  const handleAddToCart = (design) => {
    // Send analytics before mutating state
    trackEvent('cart_add', {
      designId: design?.id || null,
      eventId: design?.event_id || null,
      userId: user?.id || null,
    });
    // Use the addToCart function from CartContext
    const added = addToCart(design);
    
    if (added) {
      // Update selected package based on new cart items
      setSelectedPackage(getRecommendedPackage());
      
      // Open the cart sidebar
      setIsCartOpen(true);
    }
  };

  // Add this function to handle removing designs from cart
  const handleRemoveFromCart = (designId) => {
    trackEvent('cart_remove', {
      designId,
      userId: user?.id || null,
    });
    // Use the removeFromCart function from CartContext
    removeFromCart(designId);
    
    // Update selected package based on new cart state
    setSelectedPackage(getRecommendedPackage());
  };

  // Add this function to handle package selection
  const handleSelectPackage = (pkg) => {
    setSelectedPackage(pkg);
    if (pkg.id === 'frame' && contextCartItems.length > 0) {
      // default first image
      setSelectedFrameImageId(contextCartItems[0].id);
    }
  };

  // Add this function to handle checkout
  const handleCheckout = () => {
    trackEvent('checkout_start', {
      userId: user?.id || null,
      itemCount: contextCartItems?.length || 0,
      packageId: selectedPackage?.id || null,
      total: selectedPackage?.price || 0,
    });
    // Verify cart items exist
    if (!contextCartItems || !contextCartItems.length) {
      toast({
        title: "Empty Cart",
        description: "Please add designs to your cart first.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    console.log('Starting checkout process with items:', contextCartItems.length);
    
    try {
      // Create a fallback for issues with Stripe or API connections
      const prepareCartDataForCheckout = () => {
        // Extract event_id from cart items (all items should be from the same event)
        const event_id = contextCartItems.length > 0 ? contextCartItems[0].event_id : null;
        
        // Save cart data manually for checkout page to access
        const cartData = {
          items: contextCartItems,
          package: selectedPackage,
          total: selectedPackage?.price || 0,
          requires_shipping: selectedPackage?.id === 'frame',
          event_id: event_id,
          image_count: contextCartItems.length,
          timestamp: Date.now()
        };
        
        localStorage.setItem('playmaker_cart', JSON.stringify(cartData));
        
        // Create URL parameters for the checkout page
        const params = new URLSearchParams();
        params.append('items', contextCartItems.length);
        params.append('package', selectedPackage?.id || 'basic');
        params.append('total', selectedPackage?.price || 0);
        params.append('requires_shipping', selectedPackage?.id === 'frame' ? 'true' : 'false');
        
        // Add item IDs as a comma-separated list
        const itemIds = contextCartItems.map(item => item.id || item.databaseId).filter(Boolean).join(',');
        if (itemIds) {
          params.append('item_ids', itemIds);
        }
        
        if(selectedPackage?.id==='frame'){
          params.append('frame_color', frameColor);
          params.append('frame_image_id', selectedFrameImageId||'');
        }
        
        return `/checkout?${params.toString()}`;
      };
      
      // Use the cart context's checkout function
      const success = goToCheckout(navigate);
      
      if (!success) {
        console.log('goToCheckout returned false, using direct navigation fallback');
        // Fallback navigation if context method fails
        const checkoutUrl = prepareCartDataForCheckout();
        navigate(checkoutUrl);
      }
    } catch (error) {
      console.error('Error during checkout:', error);
      // Fallback direct navigation
      toast({
        title: "Checkout Error",
        description: "There was a problem processing your checkout. Trying fallback method.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      
      // Save cart data manually and redirect
      const fallbackEventId = contextCartItems.length > 0 ? contextCartItems[0].event_id : null;
      localStorage.setItem('playmaker_cart', JSON.stringify({
        items: contextCartItems,
        package: selectedPackage,
        total: selectedPackage?.price || 0,
        requires_shipping: selectedPackage?.id === 'frame',
        event_id: fallbackEventId,
        image_count: contextCartItems.length,
        timestamp: Date.now()
      }));
      
      navigate('/checkout');
    }
  };

  // Add this function to mark purchases after successful checkout
  const markPurchasedDesigns = async (designIds) => {
    if (!user || !designIds || !designIds.length) return;
    
    try {
      setIsLoading(true);
      
      // Update designs in Supabase
      const { data, error } = await supabase
        .from('exports')
        .update({ is_purchased: true })
        .in('id', designIds)
        .eq('user_id', user.id);
      
      if (error) {
        console.error('Error marking designs as purchased:', error);
        toast({
          title: 'Update Failed',
          description: 'There was a problem updating your purchased designs.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      
      // Refresh designs list
      await loadDesigns(designsLimit);
      
      toast({
        title: 'Purchase Successful',
        description: 'Your designs are now available for download without watermarks.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error in markPurchasedDesigns:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Add an effect to check for order completion from URL params (after Stripe redirect)
  useEffect(() => {
    const checkOrderCompletion = async () => {
      // Get URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const orderSuccess = urlParams.get('order_success');
      const orderItems = urlParams.get('items');
      
      // Only log when parameters exist to reduce console noise
      if (orderSuccess || orderItems) {
        console.log('Checking for order completion:', { 
          orderSuccess, 
          orderItems,
          url: window.location.href
        });
      }
      
      if (orderSuccess === 'true' && orderItems) {
        try {
          console.log('Order success detected! Processing purchased items...');
          
          // Parse the item IDs
          const designIds = orderItems.split(',').filter(Boolean);
          
          if (designIds.length > 0) {
            console.log(`Found ${designIds.length} design IDs to mark as purchased:`, designIds);
            
            // Display successful purchase notification
            toast({
              title: 'Purchase completed!',
              description: `${designIds.length} designs have been added to your account.`,
              status: 'success',
              duration: 5000,
              isClosable: true,
            });
            
            // Mark designs as purchased
            await markPurchasedDesigns(designIds);
            
            // Remove query parameters using history API
            const currentUrl = new URL(window.location.href);
            currentUrl.searchParams.delete('order_success');
            currentUrl.searchParams.delete('items');
            window.history.replaceState({}, '', currentUrl.toString());
          } else {
            console.warn('No valid design IDs found in order items parameter');
          }
        } catch (error) {
          console.error('Error processing order completion:', error);
          
          toast({
            title: 'Order processing issue',
            description: 'There was a problem updating your purchases. Please contact support.',
            status: 'warning',
            duration: 5000,
            isClosable: true,
          });
        }
      }
    };
    
    // Only run once after component mounts
    const checkOnce = () => {
      // Use a ref to ensure this only runs once
      if (!window._orderCompletionChecked) {
        window._orderCompletionChecked = true;
        checkOrderCompletion();
      }
    };
    
    checkOnce();
    
    // Cleanup function to allow checking again if component remounts
    return () => {
      // Reset flag after a delay to prevent multiple rapid checks
      setTimeout(() => {
        window._orderCompletionChecked = false;
      }, 5000);
    };
  }, [toast]); // Removed markPurchasedDesigns dependency to prevent re-runs

  // Automatically select a recommended package when cart items are (re)loaded – ensures the
  // checkout button is always visible, even after page refreshes or on slower devices where
  // the user hasn't manually picked a package yet.
  useEffect(() => {
    if (contextCartItems.length > 0 && !selectedPackage) {
      setSelectedPackage(getRecommendedPackage());
    }
    // If the cart becomes empty, clear the selection so the next add resets correctly.
    if (contextCartItems.length === 0 && selectedPackage) {
      setSelectedPackage(null);
    }
    // If current selection no longer fits the number of images, switch to recommended
    if (selectedPackage && selectedPackage.imageCount < contextCartItems.length && selectedPackage.id !== 'frame') {
       setSelectedPackage(getRecommendedPackage());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextCartItems]);

  // Add this early return to the component after the various state hooks but before useEffects
  // to prevent rendering the editor on checkout pages
  // Place this before the return statement but after all the state variables
  if (isCheckoutPage) {
    console.log("Dashboard component detected checkout page - skipping CESDK initialization");
    return null;
  }

  if (isEditorPage) {
    // If we're on the editor page, don't render the dashboard content
    return null;
  }

  if (!user) return null;

  // Handle loading more designs
  const handleLoadMore = async () => {
    // Calculate the current offset
    const currentOffset = savedImages.length;
    
    // Load the next batch of designs
    await loadDesigns(designsLimit, currentOffset);
  };

  const downloadPresets = [
    { id: 'instagram_story',    name: 'IG Story',     width: 1080, height: 1920, icon: FaInstagram },
    { id: 'instagram_post',     name: 'IG Square',    width: 1080, height: 1080, icon: FaInstagram },
    { id: 'instagram_portrait', name: 'IG Portrait',  width: 1080, height: 1350, icon: FaInstagram },
    { id: 'facebook_post',      name: 'Facebook',     width: 1200, height: 630,  icon: FaFacebook },
    { id: 'x_post',             name: 'X (Twitter)',  width: 1600, height: 900,  icon: FaXTwitter },
    { id: 'tiktok',             name: 'TikTok',       width: 1080, height: 1920, icon: SiTiktok },
    { id: 'snapchat_story',     name: 'Snapchat',     width: 1080, height: 1920, icon: SiSnapchat },
    { id: 'linkedin_post',      name: 'LinkedIn',     width: 1200, height: 627,  icon: FaLinkedin },
    { id: 'pinterest_pin',      name: 'Pinterest',    width: 1000, height: 1500, icon: FaPinterest },
    { id: 'youtube_thumbnail',  name: 'YouTube',      width: 1280, height: 720,  icon: FaYoutube },
    { id: 'original',           name: 'Original',     width: null,  height: null, icon: FaDownloadIcon }
  ];

  const openDownloadModal = (design) => {
    setDesignToDownload(design);
    setDownloadModalOpen(true);
  };

  const handlePresetDownload = async (preset) => {
    if (!designToDownload) return;
    try {
      setDownloadProcessing(true);

      // Original download path (no resize) – fetch blob so download attribute works cross-origin
      if (preset.id === 'original' || !preset.width || !preset.height) {
        const resp = await fetch(designToDownload.file_url || designToDownload.export_url, { mode: 'cors' });
        const blob = await resp.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${(designToDownload.template_name||'design').replace(/\s+/g,'-')}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        return;
      }

      const response = await fetch(designToDownload.file_url || designToDownload.export_url, { mode: 'cors' });
      const blob = await response.blob();
      const bitmap = await createImageBitmap(blob);

      // Create canvas with target dimensions
      const canvas = document.createElement('canvas');
      canvas.width = preset.width;
      canvas.height = preset.height;
      const ctx = canvas.getContext('2d');

      // Use 'contain' scaling so the entire design fits without cropping.
      const scale = Math.min(preset.width / bitmap.width, preset.height / bitmap.height);
      const drawnW = bitmap.width * scale;
      const drawnH = bitmap.height * scale;
      const dx = (preset.width  - drawnW) / 2;
      const dy = (preset.height - drawnH) / 2;
      ctx.drawImage(bitmap, dx, dy, bitmap.width * scale, bitmap.height * scale);

      canvas.toBlob((outBlob) => {
        if (!outBlob) return;
        const url = URL.createObjectURL(outBlob);
        const link = document.createElement('a');
        const safeName = (designToDownload.template_name || 'design').replace(/\s+/g, '-');
        link.download = `${safeName}-${preset.id}.png`;
        link.href = url;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 'image/png');
    } catch (err) {
      console.error('Preset download failed', err);
      toast({
        title: 'Download failed',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDownloadProcessing(false);
      setDownloadModalOpen(false);
    }
  };

  // Current selected event for filtering designs
  // const { currentEvent } = useCurrentEvent();

  // List of events for headers when grouping images/designs
  const { events: userEvents = [] } = useCurrentEvent();

  // Remove duplicate state declarations and effect
  // Add the banner right after the dashboard-container div opening
  return (
    <div className="dashboard-container" ref={containerRef}>
      {isImpersonating && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          backgroundColor: '#ff6b00',
          color: 'white',
          padding: '12px 20px',
          textAlign: 'center',
          zIndex: 9999,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '10px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <span style={{ flex: 1 }}>You are viewing the site as this user. Some admin features are disabled.</span>
          <button
            onClick={() => {
              // Clear URL parameters and reload
              const url = new URL(window.location.href);
              url.searchParams.delete('impersonating');
              url.searchParams.delete('admin_id');
              window.location.href = '/admin';
            }}
            style={{
              backgroundColor: 'white',
              color: '#ff6b00',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: '500',
              whiteSpace: 'nowrap'
            }}
          >
            Exit Preview Mode
          </button>
        </div>
      )}
      {/* Cart Icon - Fixed position – render on all dashboard sections */}
      <div className="cart-icon-container" onClick={() => {
        // Open the cart without trying to initialize CESDK
        setIsCartOpen(true);
        if (!cesdk && !isCartOpen) {
          console.log("Cart opened, skipping CESDK initialization");
        }
      }}>
        <FaShoppingCart className="cart-icon" />
        {contextCartItems.length > 0 && (
          <span className="cart-badge">{contextCartItems.length}</span>
        )}
      </div>

      {/* Sidebar Navigation - HIDDEN: Using AppNavigation instead */}
      <div className="sidebar" style={{ display: 'none' }}>
        <div className="logo">
          <img src={stackedLogo} alt="PlayMaker Pics" style={{ height: '60px' }} />
        </div>
        
        <nav className="main-nav">
          <ul>
            <li className={activeSection === 'dashboard' ? 'active' : ''} onClick={() => selectSection('dashboard')}>
              <a className="nav-link" href="#" onClick={(e) => { e.preventDefault(); selectSection('dashboard'); }}>
                <div className="nav-icon">
                  <i className="fa fa-home"></i>
                </div>
                <span>Dashboard DT</span>
              </a>
            </li>
            <li className={activeSection === 'images' ? 'active' : ''} onClick={() => selectSection('images')}>
              <a className="nav-link" href="#" onClick={(e) => { e.preventDefault(); selectSection('images'); }}>
                <div className="nav-icon">
                  <i className="fa fa-image"></i>
                </div>
                <span>Media Day Pics</span>
              </a>
            </li>
            {false && (
            <li className={activeSection === 'approvals' ? 'active' : ''} onClick={() => selectSection('approvals')}>
              <Link to="/dashboard/approvals" className="nav-link">
                <div className="nav-icon">
                  <i className="fa fa-check-circle"></i>
                </div>
                <span>Approvals</span>
              </Link>
            </li>
          )}

            <li className={activeSection === 'templates' ? 'active' : ''} onClick={() => selectSection('templates')}>
              <a className="nav-link" href="#" onClick={(e) => { e.preventDefault(); selectSection('templates'); }}>
                <div className="nav-icon">
                  <i className="fa fa-object-group"></i>
                </div>
                <span>Templates</span>
              </a>
            </li>
            <li>
              <Link to="/editor" className="nav-link">
                <div className="nav-icon">
                  <i className="fa fa-edit"></i>
                </div>
                <span>Editor</span>
              </Link>
            </li>
            {isAdmin && (
              <li className={activeSection === 'admin' ? 'active' : ''} onClick={() => selectSection('admin')}>
                <a className="nav-link" href="#" onClick={(e) => { e.preventDefault(); selectSection('admin'); }}>
                  <div className="nav-icon">
                    <i className="fa fa-shield"></i>
                  </div>
                  <span>Admin</span>
                </a>
              </li>
            )}
            <li className={activeSection === 'settings' ? 'active' : ''} onClick={() => selectSection('settings')}>
              <a className="nav-link" href="#" onClick={(e) => { e.preventDefault(); selectSection('settings'); }}>
                <div className="nav-icon">
                  <i className="fa fa-cog"></i>
                </div>
                <span>Settings</span>
              </a>
            </li>
          </ul>
        </nav>
        
        <div className="sidebar-footer">
          <button className="logout-button" onClick={handleLogout}>
            <i className="fa fa-sign-out"></i>
            <span>Logout</span>
          </button>
        </div>
      </div>

      {/* Hidden Dashboard Editor for CESDK initialization */}
      {!isEditorPage && !isCartOpen && (
        <div style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', height: '1px', overflow: 'hidden' }}>
          <DashboardEditor onEditorReady={handleEditorReady} />
        </div>
      )}

      {/* Main Nav/Content MOBILE*/}
      <div className="main-content">
        {/* Dashboard Section */}
        {activeSection === 'dashboard' && (
          <div className="dashboard-section">
            {/* Mobile section title */}
            <h1 className="mobile-section-title">Dashboard</h1>
            
            <div className="welcome-header">
              <div className="welcome-text">
                <h1>Welcome, {user?.user_metadata?.given_name || 'User'}!</h1>
                <p>What would you like to do today?</p>
              </div>
            </div>

            {/* Add DebugJWT component */}
            <DebugJWT />
            
            <div className="recent-activity">
              <h2>Recent Activity</h2>
              
              <div className="activity-content">
                {savedImages.length > 0 ? (
                  <div className="recent-designs">
                    <h3>Your Recent Designs</h3>
                    <div className="designs-preview">
                      {savedImages.slice(0, 4).map((design, index) => (
                        <div key={index} className="design-preview">
                          <img 
                            src={design.export_url} 
                            alt={design.template_name} 
                            onClick={() => openGalleryImage(design, index)}
                          />
                          <div className="design-name">{design.template_name}</div>
                        </div>
                      ))}
                    </div>
                    <button 
                      className="view-all-button"
                      onClick={() => selectSection('designs')}
                    >
                      View All Designs
                    </button>
                  </div>
                ) : (
                  <div className="empty-activity">
                    <p>You haven't created any designs yet.</p>
                    <button 
                      className="action-button primary"
                      onClick={() => selectSection('templates')}
                    >
                      Browse Templates
                    </button>
                  </div>
                )}
              </div>
            </div>
            
            {/* Admin-only latest events section */}
            {isAdmin && (
              <div className="admin-recent-events">
                <h2>Latest Events</h2>
                {latestEvents.length > 0 ? (
                  <>
                    <div className="events-list">
                      {latestEvents.map((event) => (
                        <div key={event.id} className="event-item">
                          <div className="event-status" data-status={event.status || 'pending'}>
                            <span className="status-indicator"></span>
                          </div>
                          <div className="event-details">
                            <h3>{event.name}</h3>
                            <div className="event-meta">
                              <span className="event-date">
                                {event.start_date ? new Date(event.start_date).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                }) : "No date"}
                              </span>
                              {(event.address || event.city) && (
                                <span className="event-location">
                                  {[
                                    event.address,
                                    event.city ? (event.state ? `${event.city}, ${event.state}` : event.city) : event.state
                                  ].filter(Boolean).join(', ')}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="event-actions">
                            <button 
                              className="view-event-button" 
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/dashboard/events/${event.id}`);
                              }}
                            >
                              View Details
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="view-all-events">
                      <button 
                        className="view-all-button"
                        onClick={() => navigate('/dashboard/events')}
                      >
                        Manage All Events
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="events-empty-state">
                    <p>No events found. Create your first event in the admin section.</p>
                    <button 
                      className="action-button primary"
                      onClick={() => navigate('/dashboard/events')}
                    >
                      Go to Events
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        
        {/* Media Day Pics Section */}
        {activeSection === 'images' && (
          <div className="images-section">
            {/* Mobile section title */}
            <h1 className="mobile-section-title">Media Day Pics</h1>
            
            <div className="section-header">
              <h2>Media Day Pics</h2>
            </div>
            
            {sourceImages.length > 0 ? (
              <>
                {Object.entries(sourceImages.reduce((acc,img)=>{const k=img.event_id||'unknown';(acc[k]=acc[k]||[]).push(img);return acc;},{})).map(([eid, imgArr]) => {
                  const eventMeta = userEvents.find((e) => e.id === eid);
                  const header = eventMeta?.name || (eid === 'unknown' ? '' : '');
                  const showHeader = Boolean(header);
                  return (
                    <div key={eid} style={{ marginBottom: '1.5rem' }}>
                      {showHeader && (
                        <h3 style={{ margin: '0.5rem 0 0.8rem', fontWeight: 600 }}>{header}</h3>
                      )}
                      <div className="pinterest-grid">
                        {imgArr.map((image, index) => (
                          <div key={index} className="pinterest-item media-day-item">
                            <div className="image-container" onClick={() => openGalleryImage(image, sourceImages.findIndex(i=>i===image))}>
                              <img 
                                src={image.url} 
                                alt="User uploaded" 
                                className="pinterest-image"
                                loading="lazy"
                              />
                              <button
                                className="design-delete-button"
                                onClick={e => {
                                  e.stopPropagation();
                                  handleDeleteImage(image.name, image.originalUrl);
                                }}
                                aria-label="Delete image"
                              >
                                <i className="fa fa-trash"></i>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </>
            ) : (
              <div className="empty-state">
                <div className="empty-icon">
                  <i className="fa fa-image"></i>
                </div>
                <h3>No Images Available</h3>
                <p>Media Day images will appear here once they are uploaded by an admin</p>
              </div>
            )}
          </div>
        )}
        
        {/* Designs Section */}
        {activeSection === 'designs' && (
          <div className="designs-section">
            {/* Mobile section title */}
            
            <div className="section-header">
              <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', whiteSpace: 'nowrap' }}>
                My Pro Edits
                {/* Info icon explaining My Pro Edits */}
                <Popover placement="bottom">
                  <PopoverTrigger>
                    <IconButton
                      icon={<FaQuestionCircle />}
                      variant="ghost"
                      size="sm"
                      aria-label="What is My Pro Edits?"
                    />
                  </PopoverTrigger>
                  <PopoverContent p={4} maxW="260px" fontSize="sm">
                    <PopoverArrow />
                    <PopoverCloseButton />
                    <strong>My Pro Edits</strong>
                    <p style={{ marginTop: '0.5rem' }}>
                      My Pro Edits is your personal gallery of professionally enhanced photos. These are the images PlayMaker's team has carefully edited with advanced AI tools and custom designs, ready for download, sharing, or printing. If you have any questions do not hesitate to reach out to our PlayMaker staff at <a href="tel:9138080407" style={{ textDecoration: 'underline' }}>913-808-0407</a>.
                    </p>
                  </PopoverContent>
                </Popover>
              </h2>
              <div className="header-actions">
                {/* Event switcher for users with multiple events */}
                <EventSwitcher />
                <div className="filter-selector">
                  <label htmlFor="designFilter">Filter: </label>
                  <select 
                    id="designFilter" 
                    value={designFilter} 
                    onChange={(e) => setDesignFilter(e.target.value)}
                    className="filter-dropdown"
                  >
                    <option value="all">All</option>
                    <option value="purchased">Purchased</option>
                    <option value="preview">Preview</option>
                  </select>
                </div>
                <div className="limit-selector">
                  <label htmlFor="designsLimit">Show: </label>
                  <select 
                    id="designsLimit" 
                    value={designsLimit} 
                    onChange={(e) => setDesignsLimit(e.target.value === 'All' ? 'All' : Number(e.target.value))}
                    className="limit-dropdown"
                  >
                    {limitOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                {/* Removed "Create New Design" button */}
              </div>
            </div>
            
            {savedImages.length > 0 ? (
              <>
                {/* AI Designs Promotion Banner */}
                <Box
                  bgGradient="linear(135deg, blue.400, purple.500)"
                  borderRadius="xl"
                  p={6}
                  mb={8}
                  color="white"
                  position="relative"
                  overflow="hidden"
                >
                  <Box
                    position="absolute"
                    top="-20px"
                    right="-20px"
                    width="80px"
                    height="80px"
                    bg="whiteAlpha.100"
                    borderRadius="50%"
                  />
                  <Box
                    position="absolute"
                    bottom="-30px"
                    left="-30px"
                    width="100px"
                    height="100px"
                    bg="whiteAlpha.50"
                    borderRadius="50%"
                  />
                  <Box position="relative" zIndex={1}>
                    <HStack spacing={3} mb={3}>
                      <Text fontSize="24px">✨</Text>
                      <Heading size="md" fontWeight="600">Want Even More Amazing Designs?</Heading>
                    </HStack>
                    <Text mb={4} opacity={0.9} fontSize="sm" lineHeight="1.4">
                      Create unlimited custom designs instantly with our AI-powered templates. Your athlete photos + our professional layouts = studio-quality results in seconds!
                    </Text>
                    <HStack spacing={3} flexWrap="wrap">
                      <Button
                        as={Link}
                        to="/templates"
                        bg="whiteAlpha.200"
                        color="white"
                        border="1px solid"
                        borderColor="whiteAlpha.300"
                        _hover={{ bg: "whiteAlpha.300", transform: "translateY(-1px)" }}
                        size="sm"
                        leftIcon={<Text fontSize="14px">🎨</Text>}
                      >
                        Explore AI Designs
                      </Button>
                      <Badge
                        bg="whiteAlpha.150"
                        color="white"
                        fontSize="xs"
                        p={2}
                        borderRadius="md"
                      >
                        🚀 New templates added weekly
                      </Badge>
                    </HStack>
                  </Box>
                </Box>

                {Object.entries(savedImages.reduce((acc,d)=>{const k=d.event_id||'unknown';(acc[k]=acc[k]||[]).push(d);return acc;},{})).map(([eid, designArr], eventIndex) => {
                  const eventMeta = userEvents.find((e) => e.id === eid);
                  const header = eventMeta?.name || (eid === 'unknown' ? '' : '');
                  const showHeader = Boolean(header);
                  const filteredDesigns = designArr
                    .filter(design => {
                      if (designFilter === 'all') return true;
                      if (designFilter === 'purchased') return design.is_purchased;
                      if (designFilter === 'preview') return !design.is_purchased;
                      return true;
                    })
                    .sort((a,b)=>{
                      if (designFilter!=='all') return 0;
                      if (a.is_purchased === b.is_purchased) return 0;
                      return a.is_purchased ? -1 : 1;
                    });

                  // Insert promotional card every 6 designs if we have enough designs
                  const designsWithPromo = [];
                  filteredDesigns.forEach((design, index) => {
                    designsWithPromo.push(design);
                    // Add promo card after every 6 designs, but not after the last one
                    if ((index + 1) % 6 === 0 && index < filteredDesigns.length - 1) {
                      designsWithPromo.push({ 
                        isPromoCard: true, 
                        id: `promo-${eventIndex}-${index}`,
                        promoType: index === 5 ? 'feature' : 'simple' // First promo is more detailed
                      });
                    }
                  });

                  return (
                    <div key={eid} style={{ marginBottom: '1.5rem' }}>
                      {showHeader && (
                        <h3 style={{ margin: '0.5rem 0 0.8rem', fontWeight: 600 }}>{header}</h3>
                      )}
                      <SimpleGrid columns={[2, 2, 3]} spacing={3}>
                        {designsWithPromo.map((item) => {
                          if (item.isPromoCard) {
                            if (item.promoType === 'feature') {
                              // Detailed promotional card
                              return (
                                <div key={item.id} style={{
                                  gridColumn: 'span 1',
                                  background: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 50%, #fecfef 100%)',
                                  borderRadius: '12px',
                                  padding: '20px',
                                  textAlign: 'center',
                                  position: 'relative',
                                  minHeight: '280px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'center',
                                  cursor: 'pointer',
                                  transition: 'transform 0.2s ease',
                                  border: '2px solid transparent'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.transform = 'translateY(-5px)';
                                  e.target.style.borderColor = 'rgba(255,255,255,0.5)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.transform = 'translateY(0)';
                                  e.target.style.borderColor = 'transparent';
                                }}
                                onClick={() => navigate('/templates')}
                                >
                                  <div style={{
                                    fontSize: '48px',
                                    marginBottom: '12px',
                                    animation: 'bounce 2s infinite'
                                  }}>
                                    🎯
                                  </div>
                                  <h4 style={{
                                    margin: '0 0 8px',
                                    color: '#333',
                                    fontSize: '16px',
                                    fontWeight: '600'
                                  }}>
                                    Create More Magic
                                  </h4>
                                  <p style={{
                                    margin: '0 0 16px',
                                    color: '#555',
                                    fontSize: '12px',
                                    lineHeight: '1.4'
                                  }}>
                                    Try our AI-powered templates for unlimited custom designs
                                  </p>
                                  <div style={{
                                    background: 'rgba(255,255,255,0.9)',
                                    color: '#333',
                                    padding: '8px 16px',
                                    borderRadius: '20px',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    display: 'inline-block'
                                  }}>
                                    🚀 Try AI Designs
                                  </div>
                                </div>
                              );
                            } else {
                              // Simple promotional card
                              return (
                                <div key={item.id} style={{
                                  background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
                                  borderRadius: '12px',
                                  padding: '16px',
                                  textAlign: 'center',
                                  minHeight: '200px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'center',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                  position: 'relative',
                                  overflow: 'hidden'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.transform = 'scale(1.02)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.transform = 'scale(1)';
                                }}
                                onClick={() => navigate('/templates')}
                                >
                                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>⚡</div>
                                  <div style={{
                                    color: '#333',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    marginBottom: '4px'
                                  }}>
                                    Need More?
                                  </div>
                                  <div style={{
                                    color: '#555',
                                    fontSize: '11px',
                                    marginBottom: '8px'
                                  }}>
                                    Create unlimited designs
                                  </div>
                                  <div style={{
                                    background: 'rgba(255,255,255,0.8)',
                                    color: '#333',
                                    padding: '6px 12px',
                                    borderRadius: '16px',
                                    fontSize: '11px',
                                    fontWeight: '500',
                                    display: 'inline-block'
                                  }}>
                                    Try AI Templates
                                  </div>
                                </div>
                              );
                            }
                          }

                          // Regular design card
                          const design = item;
                          return (
                           <div 
                             key={design.id} 
                             className="pinterest-item"
                             onClick={() => {
                               setCurrentGalleryImage(design);
                               setGalleryVisible(true);
                               setCurrentGalleryIndex(savedImages.findIndex(img => img.id === design.id));
                             }}
                           >
                             <div className="image-container">
                               <img 
                                 src={design.display_url} 
                                 alt={design.template_name || "Saved design"} 
                                 className="pinterest-image"
                               />
                               {/* Watermark / purchased badges remain */}
                               {!design.is_purchased && (
                                 <div className="watermark-badge"><i className="fa fa-lock"></i> Preview</div>
                               )}
                               {design.is_purchased && (
                                 <div className="purchased-badge"><i className="fa fa-check-circle"></i> Purchased</div>
                               )}
                             </div>
                             {/* action buttons intact */}
                             <div className="design-actions">
                               {!design.is_purchased ? (
                                 <button 
                                   className="design-cart-button" 
                                   onClick={(e) => {
                                     e.stopPropagation();
                                     handleAddToCart(design);
                                   }}
                                   aria-label="Add to cart"
                                 >
                                   <i className="fa fa-shopping-cart"></i> {window.innerWidth < 768 ? '' : 'Add to Cart'}
                                 </button>
                               ) : (
                                 <button 
                                   className="design-download-button" 
                                   onClick={(e) => {
                                     e.stopPropagation();
                                     openDownloadModal(design);
                                   }}
                                   aria-label="Download"
                                 >
                                   <i className="fa fa-download"></i> {window.innerWidth < 768 ? '' : 'Download'}
                                 </button>
                               )}
                               {isAdmin && (
                                 <button 
                                   className="design-delete-button" 
                                   onClick={(e) => {
                                     e.stopPropagation();
                                     handleDeleteDesign(design.id || design.databaseId, design.file_url);
                                   }}
                                   aria-label="Delete design"
                                 >
                                   <i className="fa fa-trash"></i>
                                 </button>
                               )}
                             </div>
                           </div>
                          );
                        })}
                      </SimpleGrid>
                    </div>
                  );
                })}

                {/* Bottom CTA - appears after all designs */}
                {savedImages.length >= 3 && (
                  <div style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: '12px',
                    padding: '30px',
                    marginTop: '30px',
                    textAlign: 'center',
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      position: 'absolute',
                      top: '0',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '200px',
                      height: '100px',
                      background: 'rgba(255,255,255,0.05)',
                      borderRadius: '50%'
                    }}></div>
                    <div style={{ position: 'relative', zIndex: 1 }}>
                      <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎨</div>
                      <h3 style={{ margin: '0 0 12px', fontSize: '22px', fontWeight: '600' }}>
                        Love Your Designs? Create More Instantly!
                      </h3>
                      <p style={{ margin: '0 0 24px', opacity: 0.9, fontSize: '16px', maxWidth: '600px', margin: '0 auto 24px' }}>
                        Don't wait for custom edits. Use our AI-powered templates to create unlimited professional designs in seconds with your athlete photos.
                      </p>
                      <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <Link 
                          to="/templates" 
                          style={{
                            background: 'white',
                            color: '#667eea',
                            padding: '14px 28px',
                            borderRadius: '8px',
                            textDecoration: 'none',
                            fontSize: '16px',
                            fontWeight: '600',
                            transition: 'all 0.2s ease',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = '0 6px 20px rgba(0,0,0,0.2)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                          }}
                        >
                          ✨ Try AI Designs Now
                        </Link>
                        <div style={{
                          background: 'rgba(255,255,255,0.2)',
                          padding: '14px 20px',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '500',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          🚀 Unlimited designs • Instant results
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="empty-state">
                <div className="empty-icon">
                  <i className="fa fa-palette"></i>
                </div>
                <h3>No Designs Yet</h3>
                <p>Create your first design using our templates or editor</p>
                <div className="empty-state-actions">
                  <Link to="/templates" className="action-button primary">
                    Browse Templates
                  </Link>
                  <Link to="/editor" className="action-button secondary">
                    Go to Editor
                  </Link>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Templates Section */}
        {activeSection === 'templates' && (
          <div className="templates-section">
            {/* Mobile section title */}
            <h1 className="mobile-section-title">AI Designs</h1>
            
            <div className="section-header">
              <h2>AI Designs</h2>
              <Link to="/editor" className="action-button secondary">
                Create New Template
              </Link>
            </div>
            
            {/* Description for AI Designs */}
            <p className="section-description">
              AI Designs are custom photo layouts automatically created by PlayMaker&apos;s app using your approved images. Our AI removes backgrounds, enhances photos, and applies them to professional sports designs crafted by our creative team. You get studio-quality results in seconds—ready to print, share, or customize.
            </p>
            {/* End description */}
            
            {templates.length > 0 ? (
              <div className="templates-grid">
                {templates.map((template) => (
                  <div key={template.id} className="template-card">
                    <div className="template-image">
                      <img src={template.thumbnail_url} alt={template.name} />
                      <div className="template-overlay">
                        <button 
                          className="use-template-button"
                          onClick={() => handleCustomizeTemplate(template)}
                        >
                          Customize
                        </button>
                      </div>
                    </div>
                    <div className="template-info">
                      <h3>{template.name}</h3>
                      <p>{template.style_category} • {template.style_subcategory || 'General'}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <div className="empty-icon">
                  <i className="fa fa-object-group"></i>
                </div>
                <h3>No Templates Available</h3>
                <p>Templates will appear here once created</p>
                <Link to="/editor" className="action-button primary">
                  Create Template
                </Link>
              </div>
            )}
          </div>
        )}
        
        {/* Admin Section */}
        {activeSection === 'admin' && isAdmin && (
          <>
            {/* Mobile section title */}
            <h1 className="mobile-section-title">Admin</h1>
            
            <ErrorBoundary>
              <AdminDashboardView />
            </ErrorBoundary>
          </>
        )}
        
        {/* Settings Section */}
        {activeSection === 'settings' && (
          <div className="settings-section">
            {/* Mobile section title */}
            <h1 className="mobile-section-title">Settings</h1>
            
            <div className="section-card">
              <h2>Account Settings</h2>
              
              {!isEditingProfile ? (
                <>
                  <div className="setting-group">
                    <label>Name</label>
                    <div className="setting-value">
                      {user?.user_metadata?.given_name || ''} {user?.user_metadata?.family_name || ''}
                    </div>
                  </div>
                  
                  <div className="setting-group">
                    <label>Email</label>
                    <div className="setting-value">{user?.email}</div>
                  </div>
                  
                  <div className="setting-actions">
                    <button 
                      className="action-button secondary"
                      onClick={handleEditProfile}
                    >
                      Edit Profile
                    </button>
                    <button 
                      className="action-button secondary"
                      onClick={() => setIsChangingPassword(true)}
                    >
                      Change Password
                    </button>
                    <button className="action-button danger" onClick={handleLogout}>
                      Log Out
                    </button>
                  </div>
                </>
              ) : (
                <form onSubmit={handleProfileUpdate}>
                  <div className="setting-group">
                    <label htmlFor="firstName">First Name</label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={profileFormData.firstName}
                      onChange={handleProfileInputChange}
                      className="setting-input"
                      required
                    />
                  </div>
                  
                  <div className="setting-group">
                    <label htmlFor="lastName">Last Name</label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={profileFormData.lastName}
                      onChange={handleProfileInputChange}
                      className="setting-input"
                      required
                    />
                  </div>
                  
                  <div className="setting-group">
                    <label htmlFor="email">Email</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={profileFormData.email}
                      className="setting-input"
                      disabled
                    />
                    <p className="setting-helper-text">Email cannot be changed</p>
                  </div>
                  
                  <div className="setting-actions">
                    <button 
                      type="submit" 
                      className="action-button primary"
                      disabled={profileUpdateLoading}
                    >
                      {profileUpdateLoading ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button 
                      type="button" 
                      className="action-button secondary"
                      onClick={() => setIsEditingProfile(false)}
                      disabled={profileUpdateLoading}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
            
            {/* Password Change Form */}
            {isChangingPassword && (
              <div className="section-card">
                <h2>Change Password</h2>
                
                <form onSubmit={handlePasswordChange}>
                  <div className="setting-group">
                    <label htmlFor="currentPassword">Current Password</label>
                    <input
                      type="password"
                      id="currentPassword"
                      name="currentPassword"
                      value={passwordFormData.currentPassword}
                      onChange={handlePasswordInputChange}
                      className="setting-input"
                      required
                    />
                  </div>
                  
                  <div className="setting-group">
                    <label htmlFor="newPassword">New Password</label>
                    <input
                      type="password"
                      id="newPassword"
                      name="newPassword"
                      value={passwordFormData.newPassword}
                      onChange={handlePasswordInputChange}
                      className="setting-input"
                      required
                      minLength={6}
                    />
                  </div>
                  
                  <div className="setting-group">
                    <label htmlFor="confirmPassword">Confirm New Password</label>
                    <input
                      type="password"
                      id="confirmPassword"
                      name="confirmPassword"
                      value={passwordFormData.confirmPassword}
                      onChange={handlePasswordInputChange}
                      className="setting-input"
                      required
                      minLength={6}
                    />
                  </div>
                  
                  <div className="setting-actions">
                    <button 
                      type="submit" 
                      className="action-button primary"
                      disabled={passwordUpdateLoading}
                    >
                      {passwordUpdateLoading ? 'Updating...' : 'Update Password'}
                    </button>
                    <button 
                      type="button" 
                      className="action-button secondary"
                      onClick={() => {
                        setIsChangingPassword(false);
                        setPasswordFormData({
                          currentPassword: '',
                          newPassword: '',
                          confirmPassword: ''
                        });
                      }}
                      disabled={passwordUpdateLoading}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        )}
        
        {/* Orders Section */}
        {activeSection === 'orders' && (
          <div className="orders-section">
            {/* Mobile section title */}
            <h1 className="mobile-section-title">My Orders</h1>
            
            <UserOrdersHistory />
          </div>
        )}
      </div>

      {/* Hidden File Input */}
      <input
        type="file"
        id="file-upload"
        accept="image/*"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {/* Loading Overlay */}
      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <p>Processing your request...</p>
        </div>
      )}

      {/* Error Toast */}
      {error && (
        <div className="error-toast">
          <p>{error}</p>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Image Gallery Modal */}
      {galleryVisible && currentGalleryImage && (
        <div 
          className="gallery-modal"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="gallery-content">
            <button 
              className="gallery-close" 
              onClick={() => setGalleryVisible(false)}
              aria-label="Close gallery"
            >
              ×
            </button>
            
            <div className="gallery-image-container">
              <img 
                src={currentGalleryImage.display_url || currentGalleryImage.export_url} 
                alt={currentGalleryImage.template_name || "Design"}
                onError={(e) => {
                  console.error('Image failed to load:', e);
                  e.target.src = 'https://via.placeholder.com/800x600?text=Image+Not+Available';
                }}
                style={{ maxWidth: '95%', maxHeight: '85vh', objectFit: 'contain' }}
              />
              
              {/* Delete button for designs */}
              {activeSection === 'designs' && isAdmin && currentGalleryImage.id && currentGalleryImage.template_name !== "User Image" && (
                <button 
                  className="gallery-delete-button" 
                  onClick={() => {
                    setGalleryVisible(false);
                    setTimeout(() => {
                      handleDeleteDesign(currentGalleryImage.id || currentGalleryImage.databaseId, currentGalleryImage.file_url || currentGalleryImage.export_url);
                    }, 300);
                  }}
                  aria-label="Delete design"
                >
                  <i className="fa fa-trash"></i>
                </button>
              )}
              
              {/* Delete button for Media Day Pics */}
              {activeSection === 'images' && currentGalleryImage.id && (
                <button 
                  className="gallery-delete-button" 
                  onClick={() => {
                    setGalleryVisible(false);
                    setTimeout(() => {
                      handleDeleteImage(currentGalleryImage.id, currentGalleryImage.export_url);
                    }, 300);
                  }}
                  aria-label="Delete image"
                >
                  <i className="fa fa-trash"></i>
                </button>
              )}
              
              {/* Add to Cart button in the top right - only for designs */}
              {activeSection === 'designs' && currentGalleryImage.id && 
                currentGalleryImage.template_name !== "User Image" && 
                !currentGalleryImage.is_purchased && (
                <div className="gallery-action-buttons top-right">
                  <button 
                    className="gallery-action-button cart icon-only"
                    onClick={() => {
                      handleAddToCart(currentGalleryImage);
                      setGalleryVisible(false);
                    }}
                    aria-label="Add to cart"
                  >
                    <i className="fa fa-cart-plus"></i>
                  </button>
                </div>
              )}
              
              {/* Watermark indicator for preview images - only for designs */}
              {activeSection === 'designs' && currentGalleryImage.id && !currentGalleryImage.is_purchased && (
                <div className="gallery-watermark-notice">
                  <i className="fa fa-lock"></i> Preview Version
                  <span className="watermark-explainer">• Purchase to download without watermark</span>
                </div>
              )}
              
              {/* Download button for purchased items - only for designs */}
              {activeSection === 'designs' && currentGalleryImage.id && currentGalleryImage.is_purchased && (
                <div className="gallery-action-buttons bottom">
                  <button 
                    className="gallery-action-button download"
                    onClick={() => {
                      setGalleryVisible(false);
                      setTimeout(()=>openDownloadModal(currentGalleryImage), 150);
                    }}
                    aria-label="Download"
                  >
                    <i className="fa fa-download"></i> Download
                  </button>
                </div>
              )}
            </div>
            
            {/* Gallery navigation */}
            {/* Check if we have multiple images to navigate through */}
            {((activeSection === 'designs' && savedImages.length > 1) || 
              (activeSection === 'images' && sourceImages.length > 1)) && (
              <div className="gallery-navigation">
                <button 
                  className="nav-button" 
                  onClick={(e) => {
                    e.stopPropagation();
                    // Determine which array to use based on active section
                    const imagesArray = activeSection === 'images' ? sourceImages : savedImages;
                    const prevIndex = (currentGalleryIndex - 1 + imagesArray.length) % imagesArray.length;
                    setCurrentGalleryIndex(prevIndex);
                    
                    if (activeSection === 'images') {
                      // For Media Day Pics
                      setCurrentGalleryImage({
                        export_url: imagesArray[prevIndex].url,
                        id: imagesArray[prevIndex].name,
                        template_name: "User Image"
                      });
                    } else {
                      // For Designs
                      setCurrentGalleryImage(imagesArray[prevIndex]);
                    }
                  }}
                  aria-label="Previous image"
                >
                  ←
                </button>
                <button 
                  className="nav-button" 
                  onClick={(e) => {
                    e.stopPropagation();
                    // Determine which array to use based on active section
                    const imagesArray = activeSection === 'images' ? sourceImages : savedImages;
                    const nextIndex = (currentGalleryIndex + 1) % imagesArray.length;
                    setCurrentGalleryIndex(nextIndex);
                    
                    if (activeSection === 'images') {
                      // For Media Day Pics
                      setCurrentGalleryImage({
                        export_url: imagesArray[nextIndex].url,
                        id: imagesArray[nextIndex].name,
                        template_name: "User Image"
                      });
                    } else {
                      // For Designs
                      setCurrentGalleryImage(imagesArray[nextIndex]);
                    }
                  }}
                  aria-label="Next image"
                >
                  →
                </button>
              </div>
            )}
            
            <div className="gallery-counter">
              {/* Show the correct counter based on active section */}
              {((activeSection === 'designs' && savedImages.length > 1) || 
                (activeSection === 'images' && sourceImages.length > 1)) && 
                `${currentGalleryIndex + 1} / ${activeSection === 'images' ? sourceImages.length : savedImages.length}`}
            </div>
            
            {swipeHintShown && 
             ((activeSection === 'designs' && savedImages.length > 1) || 
              (activeSection === 'images' && sourceImages.length > 1)) && (
              <div className="swipe-hint">Swipe to navigate through images</div>
            )}
          </div>
        </div>
      )}

      {/* Template Customization Modal */}
      {selectedTemplate && (
        <div className="customization-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h2>CUSTOMIZE {selectedTemplate?.name.toUpperCase()}</h2>
              <button 
                className="close-button"
                onClick={() => setSelectedTemplate(null)}
              >×</button>
            </div>
            
            <div className="modal-body">
              <div className="customization-options">
                <h3>CUSTOMIZATION OPTIONS</h3>
                
                {/* Render text fields dynamically */}
                {placeholderFields.filter(field => field.type === 'text').map((field) => (
                  <div className="form-group" key={field.name}>
                    <label>{field.name}</label>
                    <p className="field-description">Enter the {field.name.toLowerCase()} to display</p>
                    <input
                      type="text"
                      name={field.name}
                      value={formData[field.name] || ''}
                      onChange={handleInputChange}
                      placeholder={`Enter ${field.name}`}
                      className="form-input"
                    />
                  </div>
                ))}
                
                {/* Always show athlete image section */}
                <div className="form-group">
                  <label>ATHLETE IMAGE</label>
                  <div className="athlete-preview">
                    {primaryImage ? (
                      <div className="image-preview">
                        <img src={primaryImage} alt="Primary athlete" className="preview-thumbnail" />
                        <div className="image-info">
                          <div>USING PRIMARY IMAGE</div>
                          <div className="image-hint">YOU CAN CHANGE THIS IN THE IMAGE GALLERY SECTION</div>
                        </div>
                      </div>
                    ) : (
                      <div className="no-image-warning">
                        <p>Please select a primary image first</p>
                      </div>
                    )}
                  </div>
                </div>
                
                <button 
                  className="generate-preview-button"
                  onClick={handleGeneratePreview}
                  disabled={!primaryImage}
                >
                  Generate Preview
                </button>
              </div>
              
              <div className="preview-section">
                <h3>PREVIEW</h3>
                <div className="preview-container">
                  {isGeneratingPreview ? (
                    <div className="preview-loading">
                      <div className="loading-spinner-small"></div>
                      <p>Generating preview...</p>
                    </div>
                  ) : exportedImage ? (
                    <img 
                      src={exportedImage.localUrl} 
                      alt="Customized Template Preview" 
                      className="preview-image"
                    />
                  ) : (
                    <div className="preview-placeholder">
                      <p>CLICK "GENERATE PREVIEW" TO SEE YOUR CUSTOMIZED TEMPLATE</p>
                      <img 
                        src={selectedTemplate?.thumbnail_url} 
                        alt="Template thumbnail" 
                        className="template-thumbnail-preview"
                      />
                    </div>
                  )}
                </div>
                {exportedImage && (
                  <div className="preview-actions">
                    <button 
                      className="download-preview-button"
                      onClick={() => {
                        const link = document.createElement("a");
                        link.href = exportedImage.localUrl;
                        link.download = `${selectedTemplate.name.replace(/\s+/g, '-')}-${Date.now()}.png`;
                        link.click();
                      }}
                    >
                      Download
                    </button>
                    <button 
                      className="save-preview-button"
                      onClick={handleSaveCustomized}
                    >
                      Save to My Designs
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Shopping Cart Sidebar */}
      <div className={`shopping-cart-sidebar ${isCartOpen ? 'open' : ''}`}>
        <div className="cart-header">
          <h2>Your Shopping Cart</h2>
          <button className="close-cart" onClick={(e) => {
            e.stopPropagation();
            setIsCartOpen(false);
          }}>×</button>
        </div>
        
        <div className="cart-content">
          {contextCartItems.length === 0 ? (
            <div className="empty-cart">
              <div className="empty-cart-icon">
                <FaShoppingCart />
              </div>
              <p>Your cart is empty</p>
              <p className="empty-cart-subtext">Add designs from your collection to purchase</p>
            </div>
          ) : (
            <>
              <div className="cart-items-wrapper">
                <div className="cart-items">
                  {contextCartItems.map(item => (
                    <div key={item.id} className="cart-item">
                      <div className="cart-item-image">
                        <img src={item.display_url || item.export_url || item.url} alt={item.template_name || item.name || 'Design'} />
                      </div>
                      <div className="cart-item-details">
                        <div className="cart-item-name">{item.template_name || item.name || 'Custom Design'}</div>
                      </div>
                      <button 
                        className="remove-item" 
                        onClick={() => handleRemoveFromCart(item.id)}
                        aria-label="Remove from cart"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  ))}
                </div>

                <div className="package-selector">
                  <h3>Select a Package - 50% discount</h3>
                  <p style={{ fontSize: '0.85rem', margin: '4px 0 12px', color: '#555' }}>
                    Showing {showAllPackages ? 'all' : 'compatible'} packages for your {contextCartItems.length} design{contextCartItems.length !== 1 ? 's' : ''}. {showAllPackages ? (
                      <button onClick={() => setShowAllPackages(false)} style={{ color: '#0066ee', textDecoration: 'underline', cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}>Hide some packages</button>
                    ) : (
                      <button onClick={() => setShowAllPackages(true)} style={{ color: '#0066ee', textDecoration: 'underline', cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}>See all packages</button>
                    )}
                  </p>
                  <div className="package-options">
                    {packageOptions.map(pkg => (
                      (showAllPackages || pkg.imageCount >= contextCartItems.length || pkg.id === 'frame') && (
                        <div 
                          key={pkg.id}
                          className={`package-option ${selectedPackage?.id === pkg.id ? 'selected' : ''}`}
                          onClick={() => handleSelectPackage(pkg)}
                        >
                          <div className="package-name">{pkg.name}</div>
                          <div className="package-price" style={{ lineHeight: 1.2 }}>
                            <span style={{ textDecoration: 'line-through', color: '#888' }}>
                              ${pkg.price.toFixed(2)}
                            </span>
                            <br />
                            <span>${(pkg.price * 0.5).toFixed(2)}</span>
                          </div>
                          <div className="package-description">{pkg.description}</div>
                          {selectedPackage?.id === pkg.id && (
                            <div className="package-selected-indicator">✓</div>
                          )}
                        </div>
                      )
                    ))}
                    
                    {/* Custom package for more than 3 items */}
                    {contextCartItems.length > 3 && (
                      <div 
                        className={`package-option ${selectedPackage?.id === 'custom' ? 'selected' : ''}`}
                        onClick={() => handleSelectPackage({
                          id: 'custom',
                          name: 'Custom Package',
                          price: 65 + ((contextCartItems.length - 3) * 20),
                          imageCount: contextCartItems.length,
                          description: `${contextCartItems.length} digital images`
                        })}
                      >
                        <div className="package-name">Custom Package</div>
                        <div className="package-price" style={{ lineHeight: 1.2 }}>
                          <span style={{ textDecoration: 'line-through', color: '#888' }}>
                            {(65 + ((contextCartItems.length - 3) * 20)).toFixed(2)}
                          </span>
                          <br />
                          <span>${((65 + ((contextCartItems.length - 3) * 20)) * 0.5).toFixed(2)}</span>
                        </div>
                        <div className="package-description">{contextCartItems.length} digital images</div>
                        {selectedPackage?.id === 'custom' && (
                          <div className="package-selected-indicator">✓</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Frame specific options */}
                {selectedPackage?.id === 'frame' && (
                  <div className="frame-extra-options">
                    <h4 style={{marginTop:'1rem'}}>Frame Color</h4>
                    <div style={{display:'flex',gap:'1rem'}}>
                      <label><input type="radio" name="frameColor" value="black" checked={frameColor==='black'} onChange={()=>setFrameColor('black')} /> Black</label>
                      <label><input type="radio" name="frameColor" value="white" checked={frameColor==='white'} onChange={()=>setFrameColor('white')} /> White</label>
                      <button className="link-button" onClick={openFrameInfo} style={{marginLeft:'auto',fontSize:'0.8rem'}}>Learn more</button>
                    </div>
                    <h4 style={{marginTop:'1rem'}}>Select Design to Print</h4>
                    <div style={{display:'flex',flexWrap:'wrap',gap:'0.5rem'}}>
                      {contextCartItems.map(item => {
                        const selected = selectedFrameImageId===item.id;
                        return (
                          <div key={item.id} onClick={()=>setSelectedFrameImageId(item.id)} style={{cursor:'pointer',border:selected?'3px solid #ff6b00':'2px solid #ddd',borderRadius:'6px',padding:'2px'}}>
                            <img src={item.display_url||item.url||item.export_url} alt={item.name} style={{width:'100px',height:'80px',objectFit:'cover',borderRadius:'4px'}} />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        
        <div className="cart-footer">
          {contextCartItems.length > 0 && (
            <>
              <div className="cart-total">
                <span>Total:</span>
                {selectedPackage ? (
                  <span>
                    <span style={{ textDecoration: 'line-through', color: '#888' }}>
                      ${selectedPackage.price.toFixed(2)}
                    </span>
                    <br />
                    <span>${(selectedPackage.price * 0.5).toFixed(2)}</span>
                  </span>
                ) : (
                  <span>$0.00</span>
                )}
              </div>
              <button 
                className="checkout-button"
                onClick={handleCheckout}
                disabled={!selectedPackage}
              >
                Checkout
              </button>
            </>
          )}
        </div>
      </div>
      
      {/* Dark overlay when cart is open */}
      {isCartOpen && (
        <div className="cart-overlay" onClick={(e) => {
          e.stopPropagation();
          setIsCartOpen(false);
        }} />
      )}

      {/* Tutorial Modal disabled */}
      {/*
      {showTutorial && (
        <TutorialModal onClose={() => setShowTutorial(false)} />
      )}
      */}

      {/* Frame info modal */}
      <Modal isOpen={isFrameInfoOpen} onClose={closeFrameInfo} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>14" × 18" Frame Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text>Choose from two classic finishes. Both frames include:</Text>
              <ul style={{marginLeft:'1.2rem'}}>
                <li>Solid wood construction</li>
                <li>Acrylic glazing (safer & lighter than glass)</li>
                <li>Pre-installed hanging hardware – ready to display</li>
              </ul>
              <Image src="/images/Black Frame 14x18.jpeg" alt="Black Frame" borderRadius="md" />
              <Image src="/images/White Frame 14x18.jpeg" alt="White Frame" borderRadius="md" />
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Download Format Modal */}
      <Modal isOpen={isDownloadModalOpen} onClose={() => setDownloadModalOpen(false)} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Select Download Format</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <SimpleGrid columns={[2,3]} spacing={3}>
              {downloadPresets.map((preset) => {
                const Icon = preset.icon;
                return (
                  <Button
                    key={preset.id}
                    onClick={() => handlePresetDownload(preset)}
                    isLoading={downloadProcessing}
                    loadingText="Preparing..."
                    height="80px"
                    variant="outline"
                    flexDirection="column"
                  >
                    <Icon style={{fontSize:'24px', marginBottom:'4px'}} />
                    {preset.name}
                  </Button>
                );
              })}
            </SimpleGrid>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={() => setDownloadModalOpen(false)} disabled={downloadProcessing}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default Dashboard; 